import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStudent } from '../../services/studentService';
import { getCompliance, getComplianceStatus, getComplianceCycles, updateSection, rerunCompliance, rerunSection, getComplianceActivity, exportPdf, exportCsv, deleteComplianceCycle, assignFileToCycle, getJobStatus, getActiveJobs } from '../../services/complianceService';
import { getCompliancePollDelayMs, pollComplianceStatus, COMPLIANCE_STATUS_POLL_MAX_ATTEMPTS, sleep } from '../../hooks/useComplianceProcessing';
import { useJobStream } from '../../hooks/useJobStream';
import { useProcessingProgress } from '../../hooks/useProcessingProgress';
import { deriveCyclePointer, filterActiveJobs, normalizeJobSnapshot, pickLatestJob, withLiveCycleProgress } from '../../utils/jobProgressUtils';
import { getStudentFiles } from '../../services/fileCabinetService';
import { getIepChecklist } from '../../services/iepChecklistService';
import { getEligibilityChecklist } from '../../services/eligibilityService';
import SectionDetailModal from './SectionDetailModal';
import HumanReviewModal, {
  collectHumanReviewBySection,
  countHumanReviewFlags as countAllHumanReviewFlags,
} from './HumanReviewModal';
import ServicesSupportsMergedModal, {
  SERVICES_SUPPORTS_KEYS,
  SERVICES_SUPPORTS_MERGED_LABEL,
  SERVICES_SUPPORTS_MERGED_SUBTITLE,
} from './ServicesSupportsMergedModal';
import {
  safeNormalizeSectionExtractedData,
  summarizeAssessmentParticipationData,
  summarizeSectionExtractedData,
  filterPositiveFindings,
  deriveSectionDisplayStatus,
  deriveSectionIssueCount,
  deriveSectionCompletionPct,
  deriveOverallComplianceProgress,
  HIDE_SECTION_FINDINGS,
  countActionableHumanReviewFlags,
} from '../../constants/complianceSectionSchemas';
import ConfirmModal from '../common/ConfirmModal';
import Loader from '../common/Loader';
import { humanReviewFlagText } from '../../utils/checklistFormatting';
import Alert from '../common/Alert';
import { formatDisplayDate } from '../../utils/dateUtils';
import DocumentViewer from '../common/DocumentViewer';
import SourcePanel, { prefetchSourceDocument } from '../common/citations/SourcePanel';
import { getFileView } from '../../services/fileCabinetService';
import NavIcon from '../common/NavIcon';
import ProfileAvatar from '../common/ProfileAvatar';
import ProcessingProgressFloat from '../common/ProcessingProgressFloat';
import {
  MenuButton,
  MenuItem,
  ScoreRing,
  KeyDates,
  FilterChips,
  SectionSummaryCard,
  ActivityTimeline,
  relativeTime,
  PanelLabel,
  SectionNavItem,
  statusMeta,
  compactPages,
  PdfFileIcon,
  ExcelFileIcon,
} from './report/ReportParts';

const NAV_WIDTH_KEY = 'athenian.complianceNavWidth';
const NAV_MIN = 208;
const NAV_MAX = 460;

function readNavWidth() {
  try {
    const n = Number(window.localStorage.getItem(NAV_WIDTH_KEY));
    if (Number.isFinite(n) && n >= NAV_MIN && n <= NAV_MAX) return n;
  } catch { /* ignore */ }
  return 248;
}

function withDerivedCycleScores(cycles) {
  return (cycles || []).map((cycle) => {
    if (!cycle || typeof cycle !== 'object') return cycle;
    const stored = Number(cycle.compliance_score);
    if (Number.isFinite(stored)) return cycle;
    const hasSections = Boolean(
      cycle.identity_timeline || cycle.evaluation || cycle.iep_compliance || cycle.eligibility_compliance,
    );
    if (!hasSections) return cycle;
    const derived = deriveOverallComplianceProgress(cycle);
    if (derived == null || !Number.isFinite(Number(derived))) return cycle;
    return { ...cycle, compliance_score: derived };
  });
}

function uniqueCycles(cycles) {
  const seen = new Set();
  return (cycles || []).filter((c) => {
    const key = String(c?.id ?? `${c?.iep_date || ''}|${c?.cycle_type || ''}|${c?.iep_start_date || ''}`);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function cycleDateKey(value) {
  if (!value) return '';
  const s = String(value);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdy) return `${mdy[3]}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mergeLiveCycles(saved, liveRows, job = {}) {
  const savedList = (saved || []).filter((c) => c && typeof c === 'object');
  const savedKeys = new Set(savedList.map((c) => cycleDateKey(c.iep_date || c.iep_start_date)));
  const extra = (withLiveCycleProgress(liveRows, job) || [])
    .filter((row) => row?.anchor && !savedKeys.has(cycleDateKey(row.anchor)))
    .map((row) => ({
      id: `pending:${row.anchor}`,
      iep_date: row.anchor,
      overall_status: row.status === 'running' ? 'processing' : 'queued',
      compliance_score: null,
      _pending: true,
      cycle_type: row.status === 'running' ? 'Processing' : 'Queued',
    }));
  return uniqueCycles([...extra, ...savedList]).sort((a, b) => {
    const da = cycleDateKey(a.iep_date || a.iep_start_date);
    const db = cycleDateKey(b.iep_date || b.iep_start_date);
    return db.localeCompare(da);
  });
}


const SECTION_CONFIG = {
  identity_timeline:        { label: 'Identity & Timeline',        subtitle: 'Basic Information' },
  evaluation:               { label: 'Evaluation',                  subtitle: 'Assessment Data' },
  present_levels:           { label: 'Present Levels',              subtitle: 'PLAAFP' },
  goals:                    { label: 'Goals',                       subtitle: 'Annual Goals & Objectives' },
  services_lre:             { label: 'Services & LRE',              subtitle: 'Service minutes & LRE' },
  accommodations:           { label: 'Accommodations',              subtitle: 'Supports & modifications' },
  assessment_participation: { label: 'Assessment Participation',    subtitle: 'Statewide & LEA assessments' },
  behavior:                 { label: 'Behavior',                    subtitle: 'FBA, BIP, MDR' },
  transition_plan:          { label: 'Transition Plan',             subtitle: 'Post-Secondary Checklist' },
  iep_compliance:           { label: 'IEP Compliance',              subtitle: 'Meeting, Notice, ESY & Special Factors' },
  eligibility_compliance:   { label: 'Eligibility Compliance',      subtitle: 'Category, Determination, Meeting & Team' },
};

const DISPLAY_SECTION_KEYS = Object.keys(SECTION_CONFIG);


// Report page order; the three Services parts are one merged entry.
const SECTION_NAV_ORDER = [
  'identity_timeline', 'evaluation', 'present_levels', 'goals', 'services_supports_merged',
  'behavior', 'transition_plan', 'iep_compliance', 'eligibility_compliance',
];

function textOf(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.description || item.message || item.text || item.field || '';
}

/** The one line a section card leads with: what needs doing first, else what was found. */
function sectionTopLine(key, sec) {
  const flags = (Array.isArray(sec?.human_review_flags) ? sec.human_review_flags : [])
    .map((f) => (typeof f === 'string' ? { message: f, severity: 'warning' } : f))
    .filter((f) => f && String(f.severity || 'warning').toLowerCase() !== 'info');
  if (flags.length) return humanReviewFlagText(flags[0]) || 'Needs human review';
  const missing = (Array.isArray(sec?.missing_data) ? sec.missing_data : []).map(textOf).find(Boolean);
  if (missing) return missing;
  const rec = (Array.isArray(sec?.recommendations) ? sec.recommendations : []).map(textOf).find(Boolean);
  if (rec) return rec;
  const found = HIDE_SECTION_FINDINGS.has(key)
    ? []
    : filterPositiveFindings(Array.isArray(sec?.findings) ? sec.findings : []);
  if (found[0]?.description) return found[0].description;
  return summarizeSectionExtractedData(key, sec)[0] || 'No section data yet';
}

const CARD_BULLET_LIMIT = 4;

/**
 * Card bullets, most urgent first: missing items (issue), staff-review items (review),
 * verified findings (ok), then extracted data (data). Returns { items, more }.
 */
function sectionBullets(key, sec) {
  const all = [];
  (Array.isArray(sec?.missing_data) ? sec.missing_data : [])
    .map(textOf).filter(Boolean)
    .forEach((text) => all.push({ kind: 'issue', text }));
  (Array.isArray(sec?.human_review_flags) ? sec.human_review_flags : [])
    .map((f) => (typeof f === 'string' ? { message: f, severity: 'warning' } : f))
    .filter((f) => f && String(f.severity || 'warning').toLowerCase() !== 'info')
    .forEach((f) => all.push({ kind: 'review', text: humanReviewFlagText(f) || 'Needs human review' }));
  if (!HIDE_SECTION_FINDINGS.has(key)) {
    filterPositiveFindings(Array.isArray(sec?.findings) ? sec.findings : [])
      .forEach((f) => f?.description && all.push({ kind: 'ok', text: f.description }));
  }
  summarizeSectionExtractedData(key, sec).forEach((text) => text && all.push({ kind: 'data', text }));
  const seen = new Set();
  const unique = all.filter((b) => {
    const k = String(b.text).trim().toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return { items: unique.slice(0, CARD_BULLET_LIMIT), more: Math.max(0, unique.length - CARD_BULLET_LIMIT) };
}


function formatDate(iso) {
  if (!iso) return '—';
  try {
    return formatDisplayDate(iso);
  } catch { return '—'; }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
  } catch { return '—'; }
}


function formatCycleType(ct) {
  if (ct == null || ct === '') return '';
  const s = String(ct);
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function parseCycleDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function cycleDatesMatch(a, b) {
  const da = parseCycleDate(a);
  const db = parseCycleDate(b);
  return Boolean(da && db && da.toDateString() === db.toDateString());
}

/** IEP meeting anchor for cycle labels — the actual IEP MEETING date drives the
 *  label year. Annual-review fields are only a last resort because vision OCR can
 *  fabricate a future annual-review date; the meeting date is the ground truth. */
function resolveCycleMeetingDate(cycle) {
  if (!cycle) return null;
  const identityEd = cycle.identity_timeline?.extracted_data || {};
  const reeval = identityEd.reevaluation_due_date || cycle.reevaluation_due_date;
  const hasIepSections =
    cycle.goals?.status !== 'not_applicable' ||
    cycle.services_lre?.status !== 'not_applicable';

  if (hasIepSections) {
    const meetingCandidates = [
      identityEd.meeting_date,
      cycle.iep_date,
      cycle.iep_start_date,
      identityEd.iep_annual_review_date,
      identityEd.annual_review_date,
      identityEd.eligibility_meeting_date,
    ];
    for (const candidate of meetingCandidates) {
      if (candidate && !cycleDatesMatch(candidate, reeval)) {
        return candidate;
      }
    }
  }

  const eligibilityOnly =
    cycle.goals?.status === 'not_applicable' &&
    cycle.services_lre?.status === 'not_applicable';
  if (eligibilityOnly) {
    return (
      identityEd.eligibility_meeting_date ||
      identityEd.meeting_date ||
      reeval ||
      cycle.iep_date ||
      null
    );
  }

  return cycle.iep_date || identityEd.meeting_date || null;
}

function getCycleLabel(cycle) {
  const identityEd = cycle?.identity_timeline?.extracted_data || {};
  // Prefer the compliance row's stored iep_date — that is the cycle identity in the DB
  // and keeps the progress widget aligned with the cycle the user selected for re-run.
  // Identity meeting_date is a fallback only (OCR can drift across years in a bundle).
  const labelDate =
    cycle?.iep_date ||
    cycle?.iep_start_date ||
    identityEd.meeting_date ||
    identityEd.eligibility_meeting_date ||
    resolveCycleMeetingDate(cycle) ||
    null;
  const y = labelDate ? new Date(labelDate).getFullYear() : null;
  const year = Number.isFinite(y) ? y : '?';
  return `${year} IEP`;
}

function cycleAnchorFromCycle(cycle) {
  const raw = cycle?.iep_date || cycle?.iep_start_date || null;
  if (!raw) return null;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return null;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}


function _pagesFromEvidenceSpans(spans) {
  if (!spans || typeof spans !== 'object' || Array.isArray(spans)) return [];
  const out = [];
  Object.values(spans).forEach((val) => {
    const list = Array.isArray(val) ? val : [val];
    list.forEach((sp) => {
      if (!sp || typeof sp !== 'object') return;
      const n = Number(sp.page ?? sp.page_number);
      if (Number.isInteger(n) && n > 0) out.push(n);
    });
  });
  return out;
}

function _looksLikeSequentialPageFiller(pages) {
  if (!pages || pages.length < 8) return false;
  const s = [...new Set(pages)].sort((a, b) => a - b);
  if (s.length < 8) return false;
  return (s[s.length - 1] - s[0]) <= Math.max(s.length * 2, 12);
}

function _collectPageNums(value, out) {
  if (value == null) return;
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    out.push(value);
    return;
  }
  if (typeof value === 'string') {
    String(value).split(/[^\d]+/).forEach((part) => {
      const n = Number(part);
      if (Number.isInteger(n) && n > 0) out.push(n);
    });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => _collectPageNums(item, out));
    return;
  }
  if (typeof value === 'object') {
    if (value.page != null) _collectPageNums(value.page, out);
    if (value.pages != null) _collectPageNums(value.pages, out);
    if (value.page_number != null) _collectPageNums(value.page_number, out);
    if (value.page_numbers != null) _collectPageNums(value.page_numbers, out);
    if (value.source_pages != null) _collectPageNums(value.source_pages, out);
  }
}

function _pagesFromExtractedRecords(section) {
  const ed = section?.extracted_data;
  const out = [];
  if (!ed || typeof ed !== 'object') return out;
  const lists = [
    ed.behavior_documents,
    ed.goals,
    ed.goals_tables,
    ed.services,
    ed.adaptive_assessments,
    ed.behavioral_assessments,
  ];
  lists.forEach((list) => {
    if (Array.isArray(list)) list.forEach((row) => _collectPageNums(row, out));
  });
  const flags = section?.human_review_flags;
  if (Array.isArray(flags)) {
    flags.forEach((flag) => {
      _collectPageNums(flag?.pages, out);
      _collectPageNums(flag?.page, out);
    });
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

function _pagesFromSourceFiles(section) {
  const out = [];
  const sm = section?.source_metadata;
  if (!sm || typeof sm !== 'object') return out;
  _collectPageNums(sm.page_numbers, out);
  const files = Array.isArray(sm.files) ? sm.files : [];
  files.forEach((f) => {
    if (!f || typeof f !== 'object') return;
    _collectPageNums(f.page_numbers, out);
    _collectPageNums(f.pages, out);
  });
  return [...new Set(out)].sort((a, b) => a - b);
}

function _pagesFromFieldSourcePages(section, { maxPerField = 4 } = {}) {
  const fieldPages = section?.extracted_data?.field_source_pages;
  const fromFields = [];
  if (fieldPages && typeof fieldPages === 'object' && !Array.isArray(fieldPages)) {
    Object.entries(fieldPages).forEach(([key, raw]) => {
      const cap = (
        key === 'goals' || key === 'goals_tables' || key === 'annual_goal'
      ) ? 12 : maxPerField;
      if (Array.isArray(raw)) {
        if (raw.length > cap) return;
        fromFields.push(...raw);
      } else if (raw != null) {
        fromFields.push(raw);
      }
    });
  }
  return [...new Set(
    fromFields
      .map((n) => Number(n))
      .filter((n) => Number.isInteger(n) && n > 0),
  )].sort((a, b) => a - b);
}

function collectSectionSourcePages(section) {
  const isGoals = Array.isArray(section?.extracted_data?.goals)
    || Array.isArray(section?.extracted_data?.goals_tables);
  const fromSpans = [...new Set(_pagesFromEvidenceSpans(section?.extracted_data?._evidence_spans))]
    .sort((a, b) => a - b);
  const fromRecords = _pagesFromExtractedRecords(section);
  const fromFiles = _pagesFromSourceFiles(section);
  const fromFields = _pagesFromFieldSourcePages(section, { maxPerField: isGoals ? 12 : 4 });

  // Goals pages are consecutive IEP form pages. Union every backed cite instead
  // of discarding a tight range as "retrieval filler".
  if (isGoals) {
    return [...new Set([...fromSpans, ...fromRecords, ...fromFiles, ...fromFields])]
      .sort((a, b) => a - b);
  }

  // Prefer LLM evidence spans (quote-verified) over retrieval-inflated field pages.
  if (fromSpans.length) return fromSpans;

  // Behavior (and similar) cite pages on the extracted rows, not source_metadata.
  if (fromRecords.length) return fromRecords;

  const metaUnique = fromFiles;
  if (metaUnique.length && !_looksLikeSequentialPageFiller(metaUnique)) {
    return metaUnique;
  }

  if (fromFields.length && !_looksLikeSequentialPageFiller(fromFields)) {
    return fromFields;
  }
  // Last resort: metadata even if sequential (better than empty when no cites).
  return metaUnique;
}

function formatPageList(pages) {
  if (!pages?.length) return '';
  if (pages.length <= 6) return `p.${pages.join(', ')}`;
  const head = pages.slice(0, 5).join(', ');
  return `p.${head}, … (+${pages.length - 5} more)`;
}

function formatSectionSourcePages(section) {
  return formatPageList(collectSectionSourcePages(section));
}

/** Resolve clickable source file from enriched source_metadata (files[] or top-level). */
function resolveSectionSourceFile(section) {
  const sm = section?.source_metadata;
  if (!sm || typeof sm !== 'object') {
    return { sourceFileId: null, sourceFileName: null };
  }
  const files = Array.isArray(sm.files) ? sm.files : [];
  const withId = files.find((f) => f && (f.student_file_id || f.file_id));
  const first = withId || files[0] || null;
  const sourceFileId = (
    sm.student_file_id
    || sm.file_id
    || first?.student_file_id
    || first?.file_id
    || null
  );
  const sourceFileName = sm.file_name || first?.file_name || sm.source_file || null;
  return { sourceFileId, sourceFileName };
}

function cycleStatusDot(status) {
  if (status === 'compliant') return 'bg-green-500';
  if (status === 'needs_attention') return 'bg-amber-500';
  if (status === 'processing' || status === 'queued') return 'bg-indigo-500';
  return 'bg-red-500';
}

function cycleIdsMatch(a, b) {
  return a != null && b != null && String(a) === String(b);
}

/** Pick latest cycle (cycles[0]) or match compliance id; never keep a stale id from another student. */
function resolveDefaultCycleId(cycles, preferredId = null) {
  if (!cycles?.length) return preferredId ?? null;
  if (preferredId != null) {
    const match = cycles.find((c) => cycleIdsMatch(c.id, preferredId));
    if (match) return match.id;
  }
  return cycles[0].id;
}

function isSectionNotApplicable(section) {
  return section?.status === 'not_applicable';
}

/** Completion % for the IEP currently on screen — never a different cycle's checklist. */
function cycleScopedSectionPct(sectionKey, section) {
  if (!section || typeof section !== 'object' || Array.isArray(section)) return null;
  if (section.status === 'not_applicable') return null;
  const pct = deriveSectionCompletionPct(sectionKey, section);
  return pct == null ? null : pct;
}

function countHumanReviewFlags(compliance) {
  return countAllHumanReviewFlags(compliance, DISPLAY_SECTION_KEYS);
}

/** Warning+ human-review flags for dashboard banner (excludes info-only). */
function collectActionableHumanReviewFlags(compliance) {
  return collectHumanReviewBySection(compliance, DISPLAY_SECTION_KEYS, { actionableOnly: true })
    .flatMap(({ sectionKey, flags }) => flags.map((flag) => ({
      sectionKey,
      code: flag.code,
      message: flag.message,
    })));
}

function getOverallStatusLabel(status) {
  if (status === 'compliant') return 'Compliant';
  if (status === 'needs_attention') return 'Needs Attention';
  return 'Non-Compliant';
}



function meaningfulServiceRows(services) {
  return (services || []).filter((row) =>
    row && typeof row === 'object'
    && Object.values(row).some((v) => v != null && String(v).trim() !== ''),
  );
}

const SERVICES_SUPPORTS_SUB_LABELS = {
  services_lre: 'Services & LRE',
  accommodations: 'Accommodations',
  assessment_participation: 'Assessment',
};


const SERVICES_SUPPORTS_MAX_BULLETS = 4;

function buildServicesSupportsHighlights(compliance) {
  const primary = [];
  const extras = [];

  try {
  for (const key of SERVICES_SUPPORTS_KEYS) {
    const sec = compliance?.[key];
    const ed = sec ? safeNormalizeSectionExtractedData(key, sec) : {};
    const findings = filterPositiveFindings(sec?.findings || []);
    const hrCount = Array.isArray(sec?.human_review_flags)
      ? sec.human_review_flags.filter((f) => {
        if (!f) return false;
        if (typeof f === 'string') return true;
        return String(f.severity || 'warning').toLowerCase() !== 'info';
      }).length
      : 0;
    const status = deriveSectionDisplayStatus(key, sec);

    let dataLine = '';
    if (key === 'services_lre') {
      const count = meaningfulServiceRows(ed.services).length;
      const bits = [];
      if (count) bits.push(`${count} service${count !== 1 ? 's' : ''}`);
      if (ed.placement_type) bits.push(ed.placement_type);
      else if (ed.lre_justification) bits.push('LRE documented');
      dataLine = bits.join(' · ') || 'No services extracted';
    } else if (key === 'accommodations') {
      const bits = [];
      const inst = ed.instructional?.length || 0;
      const assess = ed.assessment?.length || 0;
      const at = ed.assistive_technology?.length || 0;
      if (inst) bits.push(`${inst} instructional`);
      if (assess) bits.push(`${assess} testing`);
      if (at) bits.push(`${at} AT`);
      dataLine = bits.join(' · ') || 'No accommodations extracted';
    } else if (key === 'assessment_participation') {
      dataLine = summarizeAssessmentParticipationData(ed);
    }

    if (hrCount > 0) {
      const hrFlags = (sec.human_review_flags || [])
        .map((f) => (typeof f === 'string' ? { message: f, severity: 'warning' } : f))
        .filter((f) => f && String(f.severity || 'warning').toLowerCase() !== 'info');
      primary.push({
        key,
        label: SERVICES_SUPPORTS_SUB_LABELS[key],
        line: humanReviewFlagText(hrFlags[0]) || 'Needs human review',
        isFailed: true,
        status,
        hasSection: !!sec,
      });
      hrFlags.slice(1).forEach((f) => {
        extras.push({
          key,
          label: SERVICES_SUPPORTS_SUB_LABELS[key],
          line: humanReviewFlagText(f) || 'Needs human review',
          isFailed: true,
          status,
          hasSection: !!sec,
          isExtra: true,
        });
      });
    } else {
      primary.push({
        key,
        label: SERVICES_SUPPORTS_SUB_LABELS[key],
        line: dataLine,
        isFailed: false,
        status,
        hasSection: !!sec,
      });
    }
  }
  } catch (err) {
    console.error('[compliance] buildServicesSupportsHighlights failed:', err);
  }

  return [...primary, ...extras].slice(0, SERVICES_SUPPORTS_MAX_BULLETS);
}


// First-load placeholder in the report's own shape, so the page does not jump when data lands.
function ReportSkeleton() {
  const bar = 'rounded bg-slate-200 dark:bg-slate-800';
  return (
    <div className="w-full min-w-0 p-3 sm:p-4 md:p-5 lg:p-6 flex flex-col gap-4 sm:gap-5 animate-pulse" aria-busy="true" aria-label="Loading compliance report">
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex items-center gap-6">
        <div className="w-[84px] h-[84px] rounded-full bg-slate-200 dark:bg-slate-800 flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <div className={`${bar} h-6 w-64`} />
          <div className={`${bar} h-14 w-full`} />
        </div>
        <div className="hidden xl:block w-[200px] space-y-2">
          <div className="h-8 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-10 rounded-[10px] bg-slate-200 dark:bg-slate-800" />
          <div className="h-9 rounded-[10px] bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
      <div className="flex gap-1.5">{[0, 1, 2, 3].map((i) => <div key={i} className="h-[34px] w-28 rounded-full bg-slate-200 dark:bg-slate-800" />)}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="min-h-[196px] rounded-[14px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
            <div className="flex justify-between gap-2">
              <div className={`${bar} h-4 w-1/2`} />
              <div className="h-5 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
            </div>
            <div className={`${bar} h-3 w-2/3`} />
            <div className={`${bar} h-1.5 w-full`} />
            <div className={`${bar} h-3 w-full`} />
            <div className={`${bar} h-3 w-5/6`} />
          </div>
        ))}
      </div>
    </div>
  );
}

const PROCESSING_STATUSES = ['initiated', 'extracting', 'analyzing', 'generating_report', 'processing', 'in_progress', 'batch_processing', 'pending'];
const OPTIMISTIC_ACTIVITY_ID = '__optimistic_processing__';
const SECTION_RERUN_DONE_STATUSES = new Set(['completed', 'failed', 'cancelled', 'warning', 'resolved']);

function activityIsProcessing(event) {
  return PROCESSING_STATUSES.includes((event?.status || '').toLowerCase());
}

function buildOptimisticProcessingActivity(jobType) {
  const jt = (jobType || 'batch_upload').toLowerCase();
  if (jt === 'batch_upload') {
    return {
      id: OPTIMISTIC_ACTIVITY_ID,
      event_type: 'compliance_check',
      status: 'processing',
      title: 'Compliance check in progress (batch)',
      description: 'Processing uploaded file(s) — results will update automatically',
      created_at: new Date().toISOString(),
    };
  }
  if (jt === 'rerun_section') {
    return {
      id: OPTIMISTIC_ACTIVITY_ID,
      event_type: 'section_update',
      status: 'processing',
      title: 'Section re-run in progress',
      description: 'Processing — results will update automatically',
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: OPTIMISTIC_ACTIVITY_ID,
    event_type: 'compliance_check',
    status: 'processing',
    title: 'Full compliance re-run in progress',
    description: 'Processing — results will update automatically',
    created_at: new Date().toISOString(),
  };
}

function mergeActivitiesForDisplay(activities, { showOptimistic, jobType }) {
  const list = (activities || []).filter((e) => e.id !== OPTIMISTIC_ACTIVITY_ID);
  if (!showOptimistic || list.some(activityIsProcessing)) return list;
  return [buildOptimisticProcessingActivity(jobType), ...list];
}

function isResultsReady(value) {
  return value === true || value === 'true' || value === 'True';
}

/** Large IEP PDFs can take 30–60+ minutes; keep polling long enough for webhook delivery. */
const STATUS_POLL_MAX_ATTEMPTS = COMPLIANCE_STATUS_POLL_MAX_ATTEMPTS;
const LONG_RUNNING_PROCESSING_MESSAGE =
  'Still processing — large documents can take up to an hour. This page will keep checking automatically.';

function normalizeSectionEventName(name) {
  return (name || '').toLowerCase().replace(/-/g, '_');
}

function isSectionRerunComplete(sectionKey, events, startedAfterMs = null) {
  const key = normalizeSectionEventName(sectionKey);
  const latest = (events || []).find(
    (e) => e.event_type === 'section_update' && normalizeSectionEventName(e.section_name) === key,
  );
  if (!latest) return false;
  if (startedAfterMs != null && latest.created_at) {
    const eventMs = new Date(latest.created_at).getTime();
    if (!Number.isNaN(eventMs) && eventMs < startedAfterMs) return false;
  }
  return SECTION_RERUN_DONE_STATUSES.has((latest.status || '').toLowerCase());
}

function getEventBadge(event) {
  const s = (event.status || '').toLowerCase();
  if (event.event_type === 'dob_mismatch' || s === 'warning') {
    return { label: 'Skipped', style: 'bg-[#FEF3C7] text-[#92400E] dark:bg-amber-900/30 dark:text-amber-400' };
  }
  if (['initiated', 'extracting', 'analyzing', 'generating_report', 'processing', 'in_progress', 'batch_processing', 'pending'].includes(s)) {
    return { label: 'Processing', style: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' };
  }
  if (s === 'failed') {
    return { label: 'Failed', style: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' };
  }
  if (s === 'needs_action') {
    return { label: 'Action Needed', style: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
  }
  if (event.event_type === 'section_update' && s === 'completed') {
    return { label: 'Resolved', style: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' };
  }
  if (event.issues_count > 0) {
    return { label: `${event.issues_count} Issue${event.issues_count !== 1 ? 's' : ''}`, style: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' };
  }
  if (event.status === 'completed') {
    return { label: 'Completed', style: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' };
  }
  return null;
}


export default function StudentComplianceReport() {
  const { id, sectionKey: routeSectionKey } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [student, setStudent] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [noCompliance, setNoCompliance] = useState(false);

  const [activities, setActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [complianceError, setComplianceError] = useState(null);
  const [assigningFile, setAssigningFile] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunningSections, setRerunningSections] = useState(new Set());
  const sectionRerunStartedRef = useRef(new Map());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [alert, setAlert] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navWidth, setNavWidth] = useState(readNavWidth);
  const navWidthRef = useRef(navWidth);
  navWidthRef.current = navWidth;
  const draggingNav = useRef(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [cardFilter, setCardFilter] = useState('all');
  const [humanReviewModal, setHumanReviewModal] = useState(null);
  const [studentFiles, setStudentFiles] = useState([]);
  const [iepCompletionScore, setIepCompletionScore] = useState(null);
  const [eligibilityScore, setEligibilityScore] = useState(null);
  const [dobMismatchDismissed, setDobMismatchDismissed] = useState(false);
  const [allCycles, setAllCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [cycleSwitching, setCycleSwitching] = useState(null);
  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false);
  const [confirmDeleteCycle, setConfirmDeleteCycle] = useState(false);
  const [confirmRerunSection, setConfirmRerunSection] = useState(null);
  const [confirmRerunAll, setConfirmRerunAll] = useState(false);
  const [deletingCycle, setDeletingCycle] = useState(false);
  const [pollGrace, setPollGrace] = useState(false);
  const [jobProgress, setJobProgress] = useState({});
  const [queueMessage, setQueueMessage] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const processingProgress = useProcessingProgress();
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);
  const [viewerHighlightQuote, setViewerHighlightQuote] = useState(null);
  // Side-by-side source panel (citation chips, page links); full viewer stays for "Full view".
  const [sourceRequest, setSourceRequest] = useState(null);
  const cycleDropdownMobileRef = useRef(null);
  const cycleDropdownTabletRef = useRef(null);
  const cycleDropdownDesktopRef = useRef(null);
  const pollGraceTimerRef = useRef(null);

  const complianceEtagRef = useRef(null);
  const skipInitialCycleRefreshRef = useRef(false);
  const prevCycleIdRef = useRef(null);

  const refreshCompliance = useCallback(async (cycleId) => {
    try {
      const res = await getCompliance(id, cycleId || selectedCycleId, complianceEtagRef.current);
      if (res.status === 304) {
        return null;
      }
      if (res.status === 404 || !res.data?.data) {
        setCompliance(null);
        setNoCompliance(true);
        return null;
      }
      const etag = res.headers?.etag;
      if (etag) complianceEtagRef.current = etag;
      setCompliance(res.data.data);
      setNoCompliance(false);
      return res.data.data;
    } catch (err) {
      if (err.response?.status === 404) {
        setNoCompliance(true);
      }
      return null;
    }
  }, [id, selectedCycleId]);

  const fetchCycles = useCallback(async (preferredId = null) => {
    try {
      const { data } = await getComplianceCycles(id);
      const cycles = withDerivedCycleScores(data?.data?.cycles || []);
      setAllCycles(cycles);
      setSelectedCycleId((prev) => resolveDefaultCycleId(cycles, preferredId ?? prev));
      return cycles;
    } catch { return []; }
  }, [id]);

  const fetchData = useCallback(async () => {
    setLoadError(null);
    try {
      const [
        studentRes,
        complianceRes,
        cyclesRes,
        activityRes,
        filesRes,
        iepRes,
        eligRes,
      ] = await Promise.allSettled([
        getStudent(id),
        getCompliance(id),
        getComplianceCycles(id),
        getComplianceActivity(id, { page: 1, limit: 5 }),
        getStudentFiles(id, { limit: 100 }),
        getIepChecklist(id),
        getEligibilityChecklist(id),
      ]);

      if (studentRes.status === 'rejected') {
        const status = studentRes.reason?.response?.status;
        if (status === 401 || status === 403) {
          setLoadError('Your session may have expired. Please log in again.');
        } else {
          setLoadError('Could not load student record. The student may not exist or the server is unavailable.');
        }
        return;
      }
      const studentData = studentRes.value?.data?.data;
      if (!studentData) {
        setLoadError('Student record returned empty. Try refreshing the page.');
        return;
      }
      setStudent(studentData);

      const cycles = cyclesRes.status === 'fulfilled'
        ? withDerivedCycleScores(cyclesRes.value.data?.data?.cycles || [])
        : [];
      setAllCycles(cycles);

      let complianceData = null;
      if (complianceRes.status === 'rejected') {
        if (complianceRes.reason?.response?.status === 404) {
          setNoCompliance(true);
          setComplianceError(null);
        } else {
          setComplianceError(complianceRes.reason?.response?.status === 429
            ? 'The server is busy. Wait a few seconds and try again.'
            : 'Could not load the compliance results.');
        }
      } else {
        setComplianceError(null);
        const complianceResponse = complianceRes.value;
        const etag = complianceResponse?.headers?.etag;
        if (etag) complianceEtagRef.current = etag;
        complianceData = complianceResponse?.data?.data ?? null;
        if (complianceData) {
          setCompliance(complianceData);
          setNoCompliance(false);
        } else {
          setCompliance(null);
          setNoCompliance(true);
        }
      }

      if (activityRes.status === 'fulfilled') {
        const activityPayload = activityRes.value?.data?.data || {};
        setActivities(activityPayload.events || []);
        setActivityPage(activityPayload.page || 1);
        setActivityTotal(activityPayload.total_pages || 0);
      }
      if (filesRes.status === 'fulfilled') {
        setStudentFiles(filesRes.value?.data?.data?.files || []);
      }
      if (iepRes.status === 'fulfilled') {
        const iepData = iepRes.value;
        if (iepData?.status === 404 || !iepData?.data?.data) {
          setIepCompletionScore(null);
        } else {
          setIepCompletionScore(iepData.data.data?.completion_score ?? null);
        }
      }
      if (eligRes.status === 'fulfilled') {
        setEligibilityScore(eligRes.value?.data?.data?.completion_score ?? null);
      }

      skipInitialCycleRefreshRef.current = true;
      setSelectedCycleId(resolveDefaultCycleId(cycles, complianceData?.id));
    } catch {
      setLoadError('Failed to load compliance report. Check your connection and try again.');
    } finally { setLoading(false); }
  }, [id]);

  const fetchActivity = useCallback(async (page = 1) => {
    try {
      const { data } = await getComplianceActivity(id, { page, limit: 5 });
      const events = data.data.events || [];
      if (page === 1) setActivities(events);
      else setActivities(prev => [...prev, ...events]);
      setActivityPage(data.data.page || page);
      setActivityTotal(data.data.total_pages || 0);
      return events;
    } catch { /* non-critical */ }
    return [];
  }, [id]);

  const fetchStudentFiles = useCallback(async () => {
    try {
      const { data } = await getStudentFiles(id, { limit: 100 });
      setStudentFiles(data.data?.files || []);
    } catch { /* non-critical */ }
  }, [id]);

  const fetchIepScore = useCallback(async () => {
    try {
      const res = await getIepChecklist(id);
      if (res.status === 404 || !res.data?.data) {
        setIepCompletionScore(null);
        return null;
      }
      setIepCompletionScore(res.data.data?.completion_score ?? null);
      return res.data.data?.completion_score ?? null;
    } catch { setIepCompletionScore(null); return null; }
  }, [id]);

  const fetchEligibilityScore = useCallback(async () => {
    try {
      const { data } = await getEligibilityChecklist(id);
      setEligibilityScore(data.data?.completion_score ?? null);
      return data.data?.completion_score ?? null;
    } catch { setEligibilityScore(null); return null; }
  }, [id]);

  const pollChecklistScores = useCallback(async () => {
    const maxAttempts = 72;
    const delayMs = 10000;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      let iepScore = null;
      let eligScore = null;
      try {
        const iepRes = await getIepChecklist(id);
        if (iepRes.status !== 404 && iepRes.data?.data) {
          iepScore = iepRes.data.data?.completion_score ?? null;
          setIepCompletionScore(iepScore);
        }
      } catch { /* checklist may not exist until background sync finishes */ }
      try {
        const { data } = await getEligibilityChecklist(id);
        eligScore = data.data?.completion_score ?? null;
        setEligibilityScore(eligScore);
      } catch { /* same */ }
      if (iepScore != null && eligScore != null) return;
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setSelectedCycleId(null);
    setAllCycles([]);
    setCompliance(null);
    setNoCompliance(false);
    complianceEtagRef.current = null;
    skipInitialCycleRefreshRef.current = false;
  }, [id]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    fetchData();
  }, [fetchData, authLoading]);

  useEffect(() => {
    if (!cycleDropdownOpen) return;
    const handleClick = (e) => {
      const inside = cycleDropdownMobileRef.current?.contains(e.target)
        || cycleDropdownTabletRef.current?.contains(e.target)
        || cycleDropdownDesktopRef.current?.contains(e.target);
      if (!inside) setCycleDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [cycleDropdownOpen]);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingNav.current) return;
      const next = Math.max(NAV_MIN, Math.min(NAV_MAX, e.clientX - 56));
      navWidthRef.current = next;
      setNavWidth(next);
    };
    const onUp = () => {
      if (!draggingNav.current) return;
      draggingNav.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { window.localStorage.setItem(NAV_WIDTH_KEY, String(navWidthRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const prevComplianceRef = useRef(null);
  const pollRef = useRef(null);
  const statusUpdatedAtRef = useRef(null);
  const awaitResultsPollRef = useRef(false);
  const processingProgressRef = useRef(processingProgress);
  processingProgressRef.current = processingProgress;
  const jobStatusPollRef = useRef(null);
  const pollStartedAtRef = useRef(Date.now());
  const restoredProgressRef = useRef(false);

  const refreshComplianceRef = useRef(refreshCompliance);
  refreshComplianceRef.current = refreshCompliance;
  const fetchActivityRef = useRef(fetchActivity);
  fetchActivityRef.current = fetchActivity;
  const fetchCyclesRef = useRef(fetchCycles);
  fetchCyclesRef.current = fetchCycles;
  const fetchIepScoreRef = useRef(fetchIepScore);
  fetchIepScoreRef.current = fetchIepScore;
  const pollChecklistScoresRef = useRef(pollChecklistScores);
  pollChecklistScoresRef.current = pollChecklistScores;

  const startPollGrace = useCallback(() => {
    setPollGrace(true);
    clearTimeout(pollGraceTimerRef.current);
    pollGraceTimerRef.current = setTimeout(() => setPollGrace(false), 120000);
  }, []);

  const finalizeJobSuccess = useCallback((payload) => {
    processingProgressRef.current.markResultsReady(payload?.message || 'Complete');
    setQueueMessage(null);
    setRerunning(false);
    setRerunningSections(new Set());
    setJobProgress({});
    startPollGrace();
    complianceEtagRef.current = null;
    refreshComplianceRef.current().then((updated) => {
      if (updated) {
        prevComplianceRef.current = updated;
        fetchCyclesRef.current();
        pollChecklistScoresRef.current();
      }
    });
    fetchActivityRef.current(1);
    setAlert({ type: 'success', message: 'Compliance check completed successfully.' });
  }, [startPollGrace]);

  const finalizeJobSuccessRef = useRef(null);
  finalizeJobSuccessRef.current = finalizeJobSuccess;

  const startAwaitResultsPoll = useCallback(() => {
    if (awaitResultsPollRef.current) return;
    awaitResultsPollRef.current = true;
    pollComplianceStatus({
      getStatus: () => getComplianceStatus(id, selectedCycleId),
      fetchActivity: () => fetchActivityRef.current(1),
      onRefresh: () => refreshComplianceRef.current(),
      maxAttempts: COMPLIANCE_STATUS_POLL_MAX_ATTEMPTS,
      shouldAbort: () => !processingProgressRef.current?.awaitingResults,
    }).then((result) => {
      awaitResultsPollRef.current = false;
      if (result.done && processingProgressRef.current?.awaitingResults) {
        finalizeJobSuccessRef.current({ message: 'Complete' });
      }
    });
  }, [id, selectedCycleId]);

  const hasProcessing = activities.some(activityIsProcessing);
  const showOptimisticActivity = (
    (processingProgress.visible || pollGrace || rerunning || rerunningSections.size > 0)
    && !hasProcessing
  );
  const displayActivities = mergeActivitiesForDisplay(activities, {
    showOptimistic: showOptimisticActivity,
    jobType: processingProgress.jobType || (rerunning ? 'rerun_compliance' : 'batch_upload'),
  });
  const shouldStream = (
    hasProcessing
    || showOptimisticActivity
    || noCompliance
    || pollGrace
    || rerunning
    || rerunningSections.size > 0
    || processingProgress.visible
  );
  // Keep polling even when SSE is connected — SSE can miss events (proxy buffering, reconnect gaps).
  const shouldPoll = shouldStream;
  const shouldPollActivity = (
    processingProgress.visible
    || pollGrace
    || rerunning
    || rerunningSections.size > 0
    || hasProcessing
  );

  useEffect(() => {
    if (authLoading || hasProcessing || processingProgress.visible) return;
    fetchIepScore();
  }, [fetchIepScore, authLoading, hasProcessing, processingProgress.visible]);

  useEffect(() => {
    if (!selectedCycleId || loading || hasProcessing || processingProgress.visible) return;
    if (String(selectedCycleId).startsWith('pending:')) return;
    if (skipInitialCycleRefreshRef.current) {
      skipInitialCycleRefreshRef.current = false;
      prevCycleIdRef.current = selectedCycleId;
      return;
    }
    const previousCycleId = prevCycleIdRef.current;
    prevCycleIdRef.current = selectedCycleId;

    if (previousCycleId == null || cycleIdsMatch(previousCycleId, selectedCycleId)) {
      refreshCompliance(selectedCycleId);
      return;
    }

    // Cycle switched: the cached ETag belongs to the previous cycle and would
    // short-circuit the fetch with a 304, leaving the old cycle's data on screen.
    complianceEtagRef.current = null;
    const targetCycle = allCycles.find((c) => cycleIdsMatch(c.id, selectedCycleId));
    setCycleSwitching(targetCycle ? getCycleLabel(targetCycle) : 'IEP cycle');
    refreshCompliance(selectedCycleId).finally(() => setCycleSwitching(null));
  }, [selectedCycleId, loading, hasProcessing, processingProgress.visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the cycle list % in lockstep with the latest-cycle score from the API.
  useEffect(() => {
    if (!selectedCycleId || !compliance || noCompliance) return;
    const stored = Number(compliance.compliance_score);
    const score = Number.isFinite(stored)
      ? stored
      : deriveOverallComplianceProgress(compliance);
    if (score == null || !Number.isFinite(Number(score))) return;
    setAllCycles((prev) => {
      let changed = false;
      const next = prev.map((c) => {
        if (!cycleIdsMatch(c.id, selectedCycleId) || c.compliance_score === score) return c;
        changed = true;
        return { ...c, compliance_score: score };
      });
      return changed ? next : prev;
    });
  }, [selectedCycleId, compliance, noCompliance]);

  const handleJobEvent = useCallback((payload) => {
    processingProgress.applyEvent(payload);
    const event = payload?.event;
    if (!event || event === 'heartbeat') return;

    if (event === 'stream_error') {
      setSseConnected(false);
      return;
    }

    if (event === 'job_queued' || event === 'job_queue_position') {
      setQueueMessage(
        payload.message
        || (payload.queue_position ? `Queued — position ${payload.queue_position}` : 'Queued for processing…'),
      );
      fetchActivity(1);
    }

    if (event === 'job_started') {
      setQueueMessage(null);
      fetchActivity(1);
      const jt = (payload.job_type || '').toLowerCase();
      if (jt.includes('rerun') && !payload.section_name && !payload.section) {
        setRerunning(true);
      }
      const sectionKey = normalizeSectionEventName(payload.section_name || payload.section);
      if (sectionKey) {
        setRerunningSections((prev) => new Set(prev).add(sectionKey));
      }
    }

    if (event === 'job_progress') {
      const sectionKey = normalizeSectionEventName(payload.section);
      if (sectionKey && payload.total) {
        setJobProgress((prev) => ({
          ...prev,
          [sectionKey]: { progress: payload.progress, total: payload.total },
        }));
        setRerunningSections((prev) => new Set(prev).add(sectionKey));
      }
    }

    if (event === 'pipeline_complete' || (event === 'job_completed' && !isResultsReady(payload.results_ready))) {
      startAwaitResultsPoll();
      return;
    }

    if (event === 'job_completed' && isResultsReady(payload.results_ready)) {
      finalizeJobSuccess(payload);
      return;
    }

    if (event === 'checklist_sync_completed') {
      if (payload.iep_score != null) setIepCompletionScore(payload.iep_score);
      if (payload.eligibility_score != null) setEligibilityScore(payload.eligibility_score);
      pollChecklistScoresRef.current();
      return;
    }

    if (event === 'job_failed') {
      setQueueMessage(null);
      setRerunning(false);
      setRerunningSections(new Set());
      setJobProgress({});
      fetchActivity(1);
      setAlert({
        type: 'error',
        message: payload.error || 'Compliance processing failed.',
      });
    }
  }, [fetchActivity, finalizeJobSuccess, processingProgress, startAwaitResultsPoll]);

  const restoreActiveJobProgress = useCallback(async () => {
    if (restoredProgressRef.current || authLoading || !id) return;
    try {
      const statusRes = await getComplianceStatus(id, selectedCycleId);
      const processing = Boolean(statusRes?.data?.data?.processing);
      const { data } = await getActiveJobs(id);
      const jobs = filterActiveJobs(data?.data?.jobs || []);
      const latest = pickLatestJob(jobs);
      let snapshot = latest ? normalizeJobSnapshot(latest) : null;

      if (latest?.job_id) {
        try {
          const detailRes = await getJobStatus(id, latest.job_id);
          const detail = detailRes?.data?.data;
          if (detail) snapshot = normalizeJobSnapshot(detail) || snapshot;
        } catch {
          /* use list snapshot */
        }
      }

      if (snapshot?.event === 'job_failed') snapshot = null;

      if (!snapshot && !processing) return;

      restoredProgressRef.current = true;
      startPollGrace();

      if (snapshot) {
        processingProgressRef.current.hydrateFromSnapshot(snapshot, { resumed: true });
        const jt = (snapshot.job_type || '').toLowerCase();
        if (jt.includes('rerun') && !snapshot.section) {
          setRerunning(true);
        }
        if (snapshot.section) {
          const sectionKey = normalizeSectionEventName(snapshot.section);
          if (sectionKey) {
            setRerunningSections((prev) => new Set(prev).add(sectionKey));
          }
        }
        const saving = snapshot.event === 'pipeline_complete'
          || snapshot.phase === 'saving'
          || snapshot.status === 'saving';
        if (saving) startAwaitResultsPoll();
      } else if (processing) {
        processingProgressRef.current.startOptimistic('batch_upload');
      }
      await fetchActivityRef.current(1);
    } catch {
      /* non-blocking — SSE / activity polling still works */
    }
  }, [
    authLoading,
    id,
    selectedCycleId,
    startAwaitResultsPoll,
    startPollGrace,
  ]);

  useEffect(() => {
    restoredProgressRef.current = false;
  }, [id]);

  useEffect(() => {
    restoreActiveJobProgress();
  }, [restoreActiveJobProgress]);

  useEffect(() => {
    if (hasProcessing && !processingProgress.visible && !pollGrace && !restoredProgressRef.current) {
      processingProgressRef.current.startOptimistic('batch_upload');
    }
  }, [hasProcessing, processingProgress.visible, pollGrace]);

  useEffect(() => {
    if (!restoredProgressRef.current || hasProcessing || !processingProgress.visible) return;
    if (!processingProgress.resumed) return;
    processingProgress.reset();
  }, [hasProcessing, processingProgress.visible, processingProgress.resumed, processingProgress.reset]);

  useEffect(() => {
    if (!processingProgress.visible) return undefined;
    const jobId = processingProgress.jobId;
    if (!jobId) return undefined;

    let cancelled = false;
    const pollJob = async () => {
      while (!cancelled) {
        try {
          const { data } = await getJobStatus(id, jobId);
          const job = data?.data;
          if (!job || cancelled) break;
          const snapshot = normalizeJobSnapshot(job);
          const ev = snapshot?.event || job.event;
          if (ev === 'job_failed' || job.status === 'failed') {
            processingProgress.applyEvent(snapshot || {
              event: 'job_failed',
              job_id: jobId,
              error: job.error,
            });
            break;
          }
          if (ev === 'job_completed' && isResultsReady(snapshot?.results_ready ?? job.results_ready)) {
            processingProgress.applyEvent({ ...(snapshot || job), results_ready: true });
            finalizeJobSuccessRef.current(snapshot || job);
            break;
          }
          if (ev === 'job_completed' && !isResultsReady(snapshot?.results_ready ?? job.results_ready)) {
            processingProgress.applyEvent({ ...(snapshot || job), event: 'job_completed', results_ready: false });
            startAwaitResultsPoll();
          } else if (ev === 'pipeline_complete' || job.status === 'saving') {
            processingProgress.applyEvent({ ...(snapshot || job), event: ev || 'pipeline_complete' });
            startAwaitResultsPoll();
          } else if (ev && ev !== 'heartbeat') {
            processingProgress.applyEvent(snapshot || job);
          }
        } catch (_) {
          // keep polling
        }
        await sleep(5000);
      }
    };
    pollJob();
    return () => { cancelled = true; };
  }, [
    processingProgress.visible,
    processingProgress.jobId,
    id,
    startAwaitResultsPoll,
  ]);

  const prevHasProcessingRef = useRef(false);

  useEffect(() => {
    const wasProcessing = prevHasProcessingRef.current;
    prevHasProcessingRef.current = hasProcessing;
    if (!wasProcessing || hasProcessing) return;

    complianceEtagRef.current = null;
    getComplianceStatus(id, selectedCycleId)
      .then((statusRes) => {
        const status = statusRes?.data?.data;
        if (status?.has_compliance || status?.compliance_updated_at) {
          finalizeJobSuccess({ message: 'Complete' });
        } else {
          refreshComplianceRef.current().then((updated) => {
            if (updated) {
              prevComplianceRef.current = updated;
              fetchCyclesRef.current();
              pollChecklistScoresRef.current();
            }
          });
          fetchActivityRef.current(1);
        }
      })
      .catch(() => fetchActivityRef.current(1));
  }, [hasProcessing, id, selectedCycleId, finalizeJobSuccess]);

  useEffect(() => {
    if (!processingProgress.visible && !hasProcessing && !pollGrace) return undefined;

    let cancelled = false;
    // Only allow the widget to finalize/clear once we've actually observed the job
    // running. A freshly-triggered run hasn't registered server-side yet, so an early
    // "no active jobs" reading must not be mistaken for completion.
    let sawActiveJob = false;
    const reconcileWidget = async () => {
      while (!cancelled) {
        try {
          const [activeRes, statusRes] = await Promise.all([
            getActiveJobs(id),
            getComplianceStatus(id, selectedCycleId),
          ]);
          const jobs = filterActiveJobs(activeRes?.data?.data?.jobs || []);
          const processing = Boolean(statusRes?.data?.data?.processing);
          const hasCompliance = Boolean(statusRes?.data?.data?.has_compliance);

          if (jobs.length > 0 || processing) {
            sawActiveJob = true;
          } else if (sawActiveJob && hasCompliance) {
            finalizeJobSuccessRef.current({ message: 'Complete' });
            break;
          } else if (sawActiveJob && !hasCompliance && processingProgressRef.current.started) {
            processingProgressRef.current.reset();
            break;
          }
        } catch (_) {
          /* retry */
        }
        await sleep(8000);
      }
    };
    reconcileWidget();
    return () => { cancelled = true; };
  }, [
    processingProgress.visible,
    processingProgress.started,
    hasProcessing,
    pollGrace,
    id,
    selectedCycleId,
  ]);

  useJobStream({
    studentId: id,
    enabled: shouldStream,
    onEvent: handleJobEvent,
    onConnectionChange: setSseConnected,
  });

  useEffect(() => {
    setRerunningSections((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      let completedLabel = null;
      for (const key of prev) {
        const startedAt = sectionRerunStartedRef.current.get(key);
        if (isSectionRerunComplete(key, activities, startedAt)) {
          next.delete(key);
          sectionRerunStartedRef.current.delete(key);
          changed = true;
          completedLabel = SECTION_CONFIG[key]?.label || key;
        }
      }
      if (changed && next.size === 0 && !rerunning && processingProgress.visible) {
        finalizeJobSuccess({
          message: completedLabel ? `${completedLabel} re-run completed.` : 'Complete',
        });
      }
      return changed ? next : prev;
    });
  }, [activities, finalizeJobSuccess, processingProgress.visible, rerunning]);

  useEffect(() => {
    return () => clearTimeout(pollGraceTimerRef.current);
  }, []);

  useEffect(() => {
    if (!shouldPollActivity) return undefined;

    let cancelled = false;
    // Skip ticks while the tab is hidden; catch up as soon as it is visible again.
    const refreshActivity = () => {
      if (!cancelled && document.visibilityState !== 'hidden') fetchActivityRef.current(1);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshActivity();
    };
    refreshActivity();
    const intervalId = setInterval(refreshActivity, 6000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shouldPollActivity]);

  useEffect(() => {
    if (!shouldPoll) return;

    let cancelled = false;
    pollStartedAtRef.current = Date.now();

    const scheduleNext = (delayMs) => {
      pollRef.current = setTimeout(runPoll, delayMs);
    };

    const runPoll = async () => {
      if (cancelled) return;

      if (typeof document !== 'undefined' && document.hidden) {
        scheduleNext(getCompliancePollDelayMs(Date.now() - pollStartedAtRef.current));
        return;
      }

      try {
        const statusRes = await getComplianceStatus(id, selectedCycleId);
        const status = statusRes?.data?.data;
        await fetchActivityRef.current(1);

        const updatedAt = status?.compliance_updated_at ?? null;
        const processing = Boolean(status?.processing);
        const prevUpdatedAt = statusUpdatedAtRef.current;
        const shouldFetchFull = !processing && (
          (updatedAt && prevUpdatedAt && updatedAt !== prevUpdatedAt)
          || (noCompliance && status?.has_compliance)
          || (prevUpdatedAt && updatedAt === prevUpdatedAt)
          || (processingProgressRef.current?.visible && status?.has_compliance)
        );

        if (shouldFetchFull) {
          complianceEtagRef.current = null;
          const updated = await refreshComplianceRef.current();
          if (updated) {
            const prev = prevComplianceRef.current;
            const isNew = !prev && updated;
            const updatedHash = JSON.stringify({
              score: updated.compliance_score,
              sections: updated.compliant_sections,
              issues: updated.total_issues,
              status: updated.overall_status,
              updated_at: updated.updated_at,
            });
            const prevHash = prev ? JSON.stringify({
              score: prev.compliance_score,
              sections: prev.compliant_sections,
              issues: prev.total_issues,
              status: prev.overall_status,
              updated_at: prev.updated_at,
            }) : '';
            const isChanged = !isNew && updatedHash !== prevHash;

            if (isNew || isChanged) {
              startPollGrace();
              const cycles = await fetchCyclesRef.current();
              if (cycles.length > 0 && !cycles.some((c) => cycleIdsMatch(c.id, selectedCycleId))) {
                setSelectedCycleId(resolveDefaultCycleId(cycles));
              }
              pollChecklistScoresRef.current();
              if (isNew) {
                setAlert({ type: 'success', message: 'Compliance data is now available.' });
              } else if (isChanged) {
                setAlert({ type: 'success', message: 'Compliance data has been updated.' });
              }
            }
            prevComplianceRef.current = updated;
            statusUpdatedAtRef.current = updated.updated_at || updatedAt;
          }
        } else if (processing && updatedAt && updatedAt !== prevUpdatedAt) {
          // Multi-IEP PDFs save one cycle at a time while still processing.
          // Reveal each newly-saved cycle without ending the processing UI.
          complianceEtagRef.current = null;
          const updated = await refreshComplianceRef.current();
          const cycles = await fetchCyclesRef.current();
          if (cycles.length > 0 && !cycles.some((c) => cycleIdsMatch(c.id, selectedCycleId))) {
            setSelectedCycleId(resolveDefaultCycleId(cycles));
          }
          if (updated) prevComplianceRef.current = updated;
          statusUpdatedAtRef.current = updated?.updated_at || updatedAt;
        } else if (updatedAt) {
          statusUpdatedAtRef.current = updatedAt;
        }
      } catch (_) {
        await fetchActivityRef.current(1);
      }

      if (!cancelled) {
        scheduleNext(getCompliancePollDelayMs(Date.now() - pollStartedAtRef.current));
      }
    };

    runPoll();
    return () => {
      cancelled = true;
      clearTimeout(pollRef.current);
    };
  }, [shouldPoll, startPollGrace, id, selectedCycleId, noCompliance]);

  const handleRerunAll = async () => {
    setRerunning(true);
    const targetCycle = allCycles.find((c) => cycleIdsMatch(c.id, selectedCycleId));
    processingProgress.startOptimistic('rerun_compliance', null, {
      cycleAnchor: cycleAnchorFromCycle(targetCycle),
      cycleTotal: allCycles.length > 1 ? allCycles.length : null,
    });
    setAlert(null);
    try {
      await rerunCompliance(id, selectedCycleId);
      fetchActivity(1);
      if (!sseConnected) {
        const result = await pollComplianceStatus({
          getStatus: () => getComplianceStatus(id, selectedCycleId),
          fetchActivity: () => fetchActivity(1),
          onRefresh: () => refreshCompliance(),
          maxAttempts: STATUS_POLL_MAX_ATTEMPTS,
        });
        setRerunning(false);
        if (result.done) {
          finalizeJobSuccess({ message: 'Complete' });
        } else {
          setAlert({ type: 'info', message: LONG_RUNNING_PROCESSING_MESSAGE });
        }
      }
    } catch (err) {
      setRerunning(false);
      processingProgress.reset();
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to run compliance check.' });
    }
  };

  const hasStudentFiles = Array.isArray(studentFiles) && studentFiles.length > 0;
  const isRerunActive = hasProcessing || rerunning || rerunningSections.size > 0;
  const NO_FILES_MESSAGE = 'No files found for this student. Please add a file before running a compliance check.';

  const requestRerunAll = () => {
    if (isRerunActive) return;
    if (!hasStudentFiles) {
      setAlert({ type: 'warning', message: NO_FILES_MESSAGE });
      return;
    }
    setConfirmRerunAll(true);
  };

  const requestRerunSection = (sectionKey) => {
    if (isRerunActive) return;
    if (!hasStudentFiles) {
      setAlert({ type: 'warning', message: NO_FILES_MESSAGE });
      return;
    }
    setConfirmRerunSection(sectionKey);
  };

  const handleRerunSection = async (sectionKey) => {
    sectionRerunStartedRef.current.set(sectionKey, Date.now());
    setRerunningSections(prev => new Set(prev).add(sectionKey));
    const targetCycle = allCycles.find((c) => cycleIdsMatch(c.id, selectedCycleId));
    processingProgress.startOptimistic('rerun_section', null, {
      cycleAnchor: cycleAnchorFromCycle(targetCycle),
      cycleTotal: allCycles.length > 1 ? allCycles.length : null,
    });
    const sectionMeta = compliance?.[sectionKey]?.source_metadata;
    const citedFiles = sectionMeta?.files?.length
      || (sectionMeta?.source_file ? sectionMeta.source_file.split(' + ').filter(Boolean).length : 0);
    const scopeHint = citedFiles > 0
      ? `${SECTION_CONFIG[sectionKey]?.label || sectionKey}: using ${citedFiles} source file(s) from this section`
      : `${SECTION_CONFIG[sectionKey]?.label || sectionKey}: using all processed files`;
    processingProgress.applyEvent({
      event: 'job_progress',
      phase: 'queued',
      message: scopeHint,
      overall_pct: 3,
      cycle_anchor: cycleAnchorFromCycle(targetCycle),
    });
    try {
      await rerunSection(id, sectionKey, null, selectedCycleId);
      fetchActivity(1);
      if (!sseConnected) {
        const result = await pollComplianceStatus({
          getStatus: () => getComplianceStatus(id, selectedCycleId),
          fetchActivity: () => fetchActivity(1),
          onRefresh: () => refreshCompliance(),
          maxAttempts: STATUS_POLL_MAX_ATTEMPTS,
        });
        sectionRerunStartedRef.current.delete(sectionKey);
        setRerunningSections(prev => { const s = new Set(prev); s.delete(sectionKey); return s; });
        if (result.done) {
          finalizeJobSuccess({
            message: `${SECTION_CONFIG[sectionKey]?.label || sectionKey} re-run completed.`,
          });
        } else {
          setAlert({ type: 'info', message: LONG_RUNNING_PROCESSING_MESSAGE });
        }
      }
    } catch (err) {
      sectionRerunStartedRef.current.delete(sectionKey);
      setRerunningSections(prev => { const s = new Set(prev); s.delete(sectionKey); return s; });
      processingProgress.reset();
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to re-run section.' });
    }
  };

  const handleDismissFinding = async (sectionKey, findingDescriptionOrPayload) => {
    try {
      const payload = typeof findingDescriptionOrPayload === 'object'
        ? findingDescriptionOrPayload
        : { dismiss_finding: findingDescriptionOrPayload };
      const { data } = await updateSection(id, sectionKey, payload, selectedCycleId);
      setCompliance(data.data);
      setAlert({ type: 'success', message: 'Removed after review.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to dismiss finding.' });
    }
  };

  const handleSaveSection = async (sectionKey, payload, options = {}) => {
    const { silent = false } = options;
    try {
      const { data } = await updateSection(id, sectionKey, payload, selectedCycleId);
      setCompliance(data.data);
      if (!silent) {
        setAlert({
          type: 'success',
          message: `${SECTION_CONFIG[sectionKey]?.label || sectionKey} updated successfully.`,
        });
      }
      fetchActivity(1);
      return data.data;
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to save section.' });
      throw err;
    }
  };

  // Stable handlers: the memoized section cards only re-render when their own data changes.
  const openSectionPage = useCallback((key) => {
    setSidebarOpen(false);
    navigate(`/students/${id}/information/section/${key}`);
  }, [navigate, id]);

  const openMergedServices = useCallback(() => {
    setSidebarOpen(false);
    navigate(`/students/${id}/information/section/services_supports_merged`);
  }, [navigate, id]);

  const openSectionHumanReview = useCallback((sectionKey) => {
    setHumanReviewModal({ sectionKey });
  }, []);

  const openMergedHumanReview = useCallback(() => {
    setHumanReviewModal({
      sectionKeys: SERVICES_SUPPORTS_KEYS,
      title: `Human review — ${SERVICES_SUPPORTS_MERGED_LABEL}`,
    });
  }, []);

  useEffect(() => {
    setActiveSection(routeSectionKey || 'overview');
  }, [routeSectionKey]);

  // Section pages are where citations are opened: start the PDF download early so
  // the source panel opens instantly instead of after the whole file arrives.
  useEffect(() => {
    if (!routeSectionKey || !studentFiles.length) return;
    const pdfFile = studentFiles.find((f) => f.mime_type === 'application/pdf');
    if (pdfFile) prefetchSourceDocument(id, pdfFile.id);
  }, [routeSectionKey, studentFiles, id]);

  const renderSectionPage = () => {
    const backToReport = () => navigate(`/students/${id}/information`);
    const isMerged = routeSectionKey === 'services_supports_merged';
    const validKey = isMerged || DISPLAY_SECTION_KEYS.includes(routeSectionKey);
    const missing = !validKey
      || (isMerged && !compliance)
      || (!isMerged && !compliance?.[routeSectionKey]);
    if (missing) {
      return (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-8 text-center max-w-2xl mx-auto">
          <p className="text-sm text-gray-500 dark:text-slate-400">This section is not available.</p>
          <button onClick={backToReport} className="mt-4 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
            Back to report
          </button>
        </div>
      );
    }
    if (isMerged) {
      return (
        <ServicesSupportsMergedModal
          variant="page"
          open
          cycleLabel={selectedCycleLabel}
          compliance={compliance}
          sectionConfig={SECTION_CONFIG}
          onClose={backToReport}
          onSave={handleSaveSection}
          onSaveComplete={() =>
            setAlert({
              type: 'success',
              message: `${SERVICES_SUPPORTS_MERGED_LABEL} updated successfully.`,
            })
          }
          onDismissFinding={handleDismissFinding}
          studentFiles={studentFiles}
          onCrossDocRerun={handleCrossDocRerun}
          onRerun={requestRerunSection}
          rerunningSections={rerunningSections}
          onOpenFileAtPage={handleOpenFileAtPage}
        onOpenSource={handleOpenSource}
        />
      );
    }
    return (
      <>
      {routeSectionKey === 'evaluation' && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <span>Tests found in the files that no IEP cycle shows in any section are listed separately.</span>
          <button type="button" onClick={() => navigate(`/students/${id}/assessment-history`)}
            className="font-semibold text-indigo-600 hover:underline dark:text-indigo-300">
            Tests not in any IEP cycle →
          </button>
        </div>
      )}
      <SectionDetailModal
        variant="page"
        open
        cycleLabel={selectedCycleLabel}
        section={compliance[routeSectionKey]}
        sectionKey={routeSectionKey}
        label={SECTION_CONFIG[routeSectionKey]?.label}
        subtitle={SECTION_CONFIG[routeSectionKey]?.subtitle}
        isRerunning={rerunningSections.has(routeSectionKey)}
        onClose={backToReport}
        onRerun={requestRerunSection}
        onSave={handleSaveSection}
        onDismissFinding={handleDismissFinding}
        studentFiles={studentFiles}
        onCrossDocRerun={handleCrossDocRerun}
        onOpenFileAtPage={handleOpenFileAtPage}
      onOpenSource={handleOpenSource}
      />
      </>
    );
  };

  // File name = "<Student Name>-<IEP cycle year>.pdf". Prefer the server's
  // Content-Disposition (authoritative), else build it from the loaded cycle.
  const buildCompliancePdfFilename = (headers) => {
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (cd) {
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      if (m && m[1]) {
        try { return decodeURIComponent(m[1].trim()); } catch { return m[1].trim(); }
      }
    }
    const name = (student?.student_name || `student_${id}`).trim();
    const year = (String(compliance?.iep_date || compliance?.iep_end_date || '')
      .match(/(19|20)\d{2}/) || [])[0];
    const safe = (name.replace(/[^\w -]/g, '').trim() || `student_${id}`).replace(/\s+/g, '_');
    return year ? `${safe}-${year}.pdf` : `${safe}.pdf`;
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    setAlert(null);
    try {
      const { data, headers } = await exportPdf(id, selectedCycleId);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildCompliancePdfFilename(headers);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setAlert({ type: 'success', message: 'Compliance PDF exported successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to export PDF.' });
    } finally {
      setExportingPdf(false);
    }
  };

  const buildComplianceExcelFilename = (headers) => {
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (cd) {
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      if (m && m[1]) {
        try { return decodeURIComponent(m[1].trim()); } catch { return m[1].trim(); }
      }
    }
    // Same pattern as PDF: <StudentName>-<YYYY>.xlsx
    const pdfName = buildCompliancePdfFilename(null);
    return pdfName.toLowerCase().endsWith('.pdf')
      ? `${pdfName.slice(0, -4)}.xlsx`
      : `${pdfName}.xlsx`;
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    setAlert(null);
    try {
      const { data, headers } = await exportCsv(id, selectedCycleId);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildComplianceExcelFilename(headers);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setAlert({ type: 'success', message: 'Compliance Excel exported successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to export Excel.' });
    } finally {
      setExportingCsv(false);
    }
  };

  const handleCrossDocRerun = async (sectionKey, documentIds) => {
    const startedAt = Date.now();
    sectionRerunStartedRef.current.set(sectionKey, startedAt);
    setRerunningSections(prev => new Set(prev).add(sectionKey));
    const targetCycle = allCycles.find((c) => cycleIdsMatch(c.id, selectedCycleId));
    processingProgress.startOptimistic('rerun_section', null, {
      cycleAnchor: cycleAnchorFromCycle(targetCycle),
      cycleTotal: allCycles.length > 1 ? allCycles.length : null,
    });
    try {
      const body = {
        document_ids: documentIds?.length ? documentIds : null,
        previous_data: compliance?.[sectionKey] || null,
      };
      await rerunSection(id, sectionKey, body, selectedCycleId);
      fetchActivity(1);
      if (!sseConnected) {
        const result = await pollComplianceStatus({
          getStatus: () => getComplianceStatus(id, selectedCycleId),
          fetchActivity: () => fetchActivity(1),
          onRefresh: () => refreshCompliance(),
          maxAttempts: STATUS_POLL_MAX_ATTEMPTS,
        });
        sectionRerunStartedRef.current.delete(sectionKey);
        setRerunningSections(prev => { const s = new Set(prev); s.delete(sectionKey); return s; });
        const docCount = documentIds?.length || 0;
        const docLabel = docCount > 0 ? ` with ${docCount} document${docCount !== 1 ? 's' : ''}` : '';
        if (result.done) {
          finalizeJobSuccess({
            message: `${SECTION_CONFIG[sectionKey]?.label || sectionKey} re-evaluated${docLabel}.`,
          });
        } else {
          setAlert({ type: 'info', message: LONG_RUNNING_PROCESSING_MESSAGE });
        }
      }
    } catch (err) {
      sectionRerunStartedRef.current.delete(sectionKey);
      setRerunningSections(prev => { const s = new Set(prev); s.delete(sectionKey); return s; });
      processingProgress.reset();
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to re-run section with documents.' });
    }
  };

  const handleDeleteCycle = async () => {
    if (!selectedCycleId || deletingCycle) return;
    setDeletingCycle(true);
    try {
      const { data } = await deleteComplianceCycle(id, selectedCycleId);
      const result = data.data || {};
      setConfirmDeleteCycle(false);
      setAlert({ type: 'success', message: 'Compliance cycle deleted successfully.' });
      const cycles = await fetchCycles();
      const allGone = result.cycles_remaining === 0 || cycles.length === 0;
      if (allGone) {
        setCompliance(null);
        setNoCompliance(true);
        setSelectedCycleId(null);
        setIepCompletionScore(null);
        setEligibilityScore(null);
        setStudent((prev) => (prev ? { ...prev, compliance_score: 0 } : prev));
      } else {
        const newId = result.new_latest_id || cycles[0]?.id;
        setSelectedCycleId(newId);
      }
      await fetchActivity(1);
      if (allGone) {
        await fetchIepScore();
      } else if (result.was_latest) {
        await Promise.all([fetchIepScore(), fetchEligibilityScore()]);
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete compliance cycle.' });
    } finally {
      setDeletingCycle(false);
    }
  };

  const handleAssignFile = async (fileId, cycleId) => {
    if (assigningFile) return;
    setAssigningFile({ fileId, cycleId });
    try {
      const { data } = await assignFileToCycle(id, fileId, cycleId);
      const payload = data?.data || data;
      const merged = payload?.file_merged !== false;
      setSelectedCycleId(cycleId);
      await refreshCompliance(cycleId);
      await Promise.all([fetchActivity(1), fetchStudentFiles()]);
      setAlert({
        type: merged ? 'success' : 'warning',
        message: merged
          ? 'File assigned and merged into the selected IEP cycle. Review updated sections above.'
          : (payload?.merge_note || 'File assigned to cycle. Click Re-run to extract compliance data from this document.'),
      });
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.response?.data?.detail || err.response?.data?.message || 'Failed to assign file to cycle.',
      });
    } finally {
      setAssigningFile(null);
    }
  };

  /** Student-file id for a cite: numeric id as-is, document UUID via the file list. */
  const resolveCiteFile = (fileId, fileName) => {
    const byDoc = fileId != null && !/^\d+$/.test(String(fileId))
      ? studentFiles.find((f) => String(f.document_id) === String(fileId))
      : null;
    if (byDoc) return { id: byDoc.id, name: fileName || byDoc.file_name };
    if (fileId != null && fileId !== '' && /^\d+$/.test(String(fileId))) {
      const f = studentFiles.find((x) => String(x.id) === String(fileId));
      return { id: Number(fileId), name: fileName || f?.file_name, mime: f?.mime_type };
    }
    const fallback = studentFiles.find((f) => {
      const t = String(f.file_type || f.document_type || '').toUpperCase();
      return t.includes('IEP') || t.includes('EVAL') || t.includes('PDF') || !t;
    }) || studentFiles[0];
    return { id: fallback?.id ?? null, name: fileName || fallback?.file_name, mime: fallback?.mime_type };
  };

  const handleOpenSource = (req) => {
    const f = resolveCiteFile(req?.fileId, req?.fileName);
    if (!f.id) {
      setAlert({ type: 'error', message: 'Could not find the source file for this citation.' });
      return;
    }
    if (f.mime && f.mime !== 'application/pdf') {
      openFullViewer(f.id, req.page, f.name, req.quote);
      return;
    }
    setSourceRequest({ ...req, fileId: f.id, fileName: f.name || 'Document' });
  };

  const handleOpenFileAtPage = (fileId, page, fileName, highlightQuote = null) => {
    if (!page) {
      openFullViewer(fileId, page, fileName, highlightQuote);
      return;
    }
    handleOpenSource({ fileId, page, fileName, quote: highlightQuote });
  };

  const openFullViewer = async (fileId, page, fileName, highlightQuote = null) => {
    const hasExplicitFile = fileId != null && fileId !== '';
    const fallbackFile = studentFiles.find((f) => {
      const t = String(f.file_type || f.document_type || '').toUpperCase();
      return t.includes('IEP') || t.includes('EVAL') || t.includes('PDF') || !t;
    }) || studentFiles[0];
    const resolvedId = hasExplicitFile
      ? fileId
      : (fallbackFile?.id || fallbackFile?.student_file_id);
    const resolvedName = fileName || fallbackFile?.file_name || fallbackFile?.name;
    try {
      if (!resolvedId) throw new Error('missing file');
      const { data } = await getFileView(id, resolvedId, page);
      const fileData = data.data || data;
      setViewerFile({
        file_name: fileData.file_name || resolvedName || 'Document',
        file_url: fileData.file_url,
        mime_type: fileData.mime_type || 'application/pdf',
        file_type: 'IEP',
        student_id: id,
        id: fileData.student_file_id || resolvedId,
        student_file_id: fileData.student_file_id || resolvedId,
        document_id: fileData.document_id,
      });
      setViewerPage(fileData.page || page || null);
      setViewerHighlightQuote(highlightQuote || null);
    } catch {
      const matchedFile = studentFiles.find(f => f.id === resolvedId || f.id === Number(resolvedId))
        || fallbackFile;
      if (matchedFile) {
        setViewerFile(matchedFile);
        setViewerPage(page || null);
        setViewerHighlightQuote(highlightQuote || null);
      } else {
        setAlert({ type: 'error', message: 'Could not load file for viewing.' });
      }
    }
  };

  const showMainContent = !loading && !!student && !loadError;

  const cycleList = mergeLiveCycles(
    allCycles,
    processingProgress.visible ? processingProgress.cycles : null,
    processingProgress,
  );
  const livePointer = processingProgress.visible ? deriveCyclePointer(processingProgress) : null;
  const selectedCycle = showMainContent && selectedCycleId != null
    ? cycleList.find((c) => cycleIdsMatch(c.id, selectedCycleId))
    : null;
  const isLatestCycle = showMainContent && cycleList.length > 0 && cycleIdsMatch(cycleList[0]?.id, selectedCycleId);
  const floatCycle = (selectedCycleId != null
    && allCycles.find((c) => cycleIdsMatch(c.id, selectedCycleId)))
    || allCycles[0]
    || null;
  // Prefer the cycle the job was started for (optimistic/SSE anchor), else the
  // selected cycle in the dropdown — never silently fall back to another cycle's year.
  const jobCycleYear = (livePointer?.cycleAnchor || processingProgress.cycleAnchor)
    ? (() => {
      const raw = String(livePointer?.cycleAnchor || processingProgress.cycleAnchor);
      const m = raw.match(/(\d{4})/);
      if (m) return Number(m[1]);
      const y = new Date(raw).getFullYear();
      return Number.isFinite(y) ? y : null;
    })()
    : null;
  const selectedCycleLabel = selectedCycle ? getCycleLabel(selectedCycle) : null;
  const liveCycleLabel = livePointer?.cycleLabel
    || (jobCycleYear ? `${jobCycleYear} IEP` : (selectedCycleLabel || (floatCycle ? getCycleLabel(floatCycle) : null)));
  const liveCycleActive = processingProgress.visible
    && Number(livePointer?.cycleTotal || processingProgress.cycleTotal) > 1;
  // Multi-IEP progressive delivery: a background full-cycle job may be processing a
  // DIFFERENT cycle than the one on screen. The live per-section badge must only appear
  // on the cycle actually being processed, never bleed onto an already-delivered cycle
  // the user is viewing. Section re-runs always target the selected cycle.
  const sectionProgressMatchesView = !liveCycleActive
    || (selectedCycleLabel != null && selectedCycleLabel === liveCycleLabel);
  const score = showMainContent
    ? (noCompliance
      ? 0
      : (Number.isFinite(Number(compliance?.compliance_score))
        ? Number(compliance.compliance_score)
        : (deriveOverallComplianceProgress(compliance)
          ?? student.compliance_score
          ?? 0)))
    : null;
  const cycleIepPct = showMainContent && compliance
    ? cycleScopedSectionPct('iep_compliance', compliance.iep_compliance)
    : null;
  const cycleEligPct = showMainContent && compliance
    ? cycleScopedSectionPct('eligibility_compliance', compliance.eligibility_compliance)
    : null;
  const displayIepPct = cycleIepPct != null ? cycleIepPct : (isLatestCycle ? iepCompletionScore : null);
  const displayEligPct = cycleEligPct != null ? cycleEligPct : (isLatestCycle ? eligibilityScore : null);
  const overallStatus = showMainContent ? (compliance?.overall_status || 'non_compliant') : 'non_compliant';
  const gradeLabel = student?.grade ? `Grade ${student.grade}` : '—';
  const dobLabel = formatDate(
    compliance?.identity_timeline?.extracted_data?.date_of_birth
    || student?.date_of_birth
  );
  const humanReviewCount = showMainContent ? countHumanReviewFlags(compliance) : 0;
  const actionableHumanReviewFlags = showMainContent
    ? collectActionableHumanReviewFlags(compliance)
    : [];

  const navSections = showMainContent
    ? DISPLAY_SECTION_KEYS.filter((key) => !isSectionNotApplicable(compliance?.[key])).map(key => {
        const sec = compliance?.[key];
        const pageList = collectSectionSourcePages(sec);
        const { sourceFileId, sourceFileName } = resolveSectionSourceFile(sec);
        return {
          key,
          label: SECTION_CONFIG[key].label,
          issues: deriveSectionIssueCount(key, sec),
          pages: formatSectionSourcePages(sec),
          pageList,
          sourceFileId,
          sourceFileName,
        };
      })
    : DISPLAY_SECTION_KEYS.map(key => ({
        key,
        label: SECTION_CONFIG[key].label,
        subtitle: SECTION_CONFIG[key].subtitle,
        issues: 0,
        pages: '',
        pageList: [],
        sourceFileId: null,
        sourceFileName: null,
      }));
  const navSectionByKey = Object.fromEntries(navSections.map((s) => [s.key, s]));

  // One entry per card / nav row; the three Services parts show as one merged entry.
  const sectionEntries = [];
  if (showMainContent && compliance) {
    SECTION_NAV_ORDER.forEach((key) => {
      if (key === 'services_supports_merged') {
        const parts = SERVICES_SUPPORTS_KEYS.map((k) => [k, compliance[k]]).filter(([, sec]) => sec);
        const statuses = parts.map(([k, sec]) => deriveSectionDisplayStatus(k, sec));
        const pcts = parts.map(([k, sec]) => deriveSectionCompletionPct(k, sec)).filter((p) => p != null);
        const pageList = [...new Set(parts.flatMap(([k]) => navSectionByKey[k]?.pageList || []))].sort((a, b) => a - b);
        const source = SERVICES_SUPPORTS_KEYS.map((k) => navSectionByKey[k]).find((s) => s?.sourceFileId)
          || SERVICES_SUPPORTS_KEYS.map((k) => navSectionByKey[k]).find(Boolean);
        const highlight = buildServicesSupportsHighlights(compliance)[0];
        const prog = SERVICES_SUPPORTS_KEYS.map((k) => jobProgress[k]).find((p) => p?.total);
        sectionEntries.push({
          key,
          label: SERVICES_SUPPORTS_MERGED_LABEL,
          subtitle: SERVICES_SUPPORTS_MERGED_SUBTITLE,
          status: statuses.includes('non_compliant') ? 'non_compliant'
            : statuses.includes('needs_attention') ? 'needs_attention' : 'compliant',
          issues: parts.reduce((n, [k, sec]) => n + deriveSectionIssueCount(k, sec), 0),
          review: parts.reduce((n, [, sec]) => n + countActionableHumanReviewFlags(sec), 0),
          pct: pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0,
          top: highlight ? `${highlight.label}: ${highlight.line}` : 'No section data yet',
          bullets: {
            items: buildServicesSupportsHighlights(compliance).map((h) => ({
              kind: h.isFailed ? 'review' : h.status === 'non_compliant' ? 'issue' : 'data',
              label: h.isExtra ? null : h.label,
              text: h.line,
            })),
            more: 0,
          },
          pageList,
          pagesText: compactPages(pageList),
          sourceFileId: source?.sourceFileId || null,
          sourceFileName: source?.sourceFileName || null,
          isRunning: sectionProgressMatchesView && SERVICES_SUPPORTS_KEYS.some((k) => rerunningSections.has(k)),
          runningLabel: prog ? `${prog.progress}/${prog.total}` : undefined,
        });
        return;
      }
      // Every section gets a card, as before: a not-applicable section (e.g. a
      // Transition Plan under age 14) shows as compliant with its reason.
      const raw = compliance[key];
      const sec = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
      const notApplicable = isSectionNotApplicable(sec);
      const nav = navSectionByKey[key];
      sectionEntries.push({
        key,
        label: SECTION_CONFIG[key].label,
        subtitle: SECTION_CONFIG[key].subtitle,
        status: notApplicable ? 'compliant' : deriveSectionDisplayStatus(key, sec),
        issues: deriveSectionIssueCount(key, sec),
        review: countActionableHumanReviewFlags(sec),
        pct: notApplicable ? 100 : (deriveSectionCompletionPct(key, sec) ?? 0),
        top: sectionTopLine(key, sec),
        bullets: sectionBullets(key, sec),
        pageList: nav?.pageList || [],
        pagesText: compactPages(nav?.pageList || []),
        sourceFileId: nav?.sourceFileId || null,
        sourceFileName: nav?.sourceFileName || null,
        isRunning: rerunningSections.has(key) && sectionProgressMatchesView,
        runningLabel: jobProgress[key]?.total ? `${jobProgress[key].progress}/${jobProgress[key].total}` : undefined,
      });
    });
  }
  const chipCounts = {
    all: sectionEntries.length,
    issues: sectionEntries.filter((e) => e.issues > 0).length,
    review: sectionEntries.filter((e) => e.review > 0).length,
    compliant: sectionEntries.filter((e) => e.status === 'compliant').length,
  };
  const visibleEntries = sectionEntries
    .filter((e) => (cardFilter === 'issues' ? e.issues > 0
      : cardFilter === 'review' ? e.review > 0
        : cardFilter === 'compliant' ? e.status === 'compliant' : true));
  const openEntry = (key) => {
    setSidebarOpen(false);
    if (key === 'services_supports_merged') openMergedServices();
    else openSectionPage(key);
  };
  const reviewEntry = (key) => {
    if (key === 'services_supports_merged') openMergedHumanReview();
    else openSectionHumanReview(key);
  };
  const openEntryPage = (entry, page) => {
    const target = page ?? entry.pageList?.[0];
    if (target != null) handleOpenFileAtPage(entry.sourceFileId, target, entry.sourceFileName);
  };

  // Activity timeline rows (right status pane on wide screens, a card below on smaller ones).
  const activityItems = displayActivities.map((event) => {
    const badge = getEventBadge(event);
    const isDateNotFound = event.event_type === 'date_not_found' && event.metadata;
    const isIdentityResolved = event.event_type === 'identity_mismatch'
      && (event.status || '').toLowerCase() === 'completed';
    const label = badge?.label || '';
    const tone = label === 'Processing' ? 'processing'
      : label === 'Failed' ? 'failed'
        : (label === 'Skipped' || label === 'Action Needed' || ((isDateNotFound || event.event_type === 'dob_mismatch') && !isIdentityResolved)) ? 'warning'
          : (label === 'Completed' || label === 'Resolved' || isIdentityResolved) ? 'ok' : 'info';
    const when = event.completed_at || event.created_at;
    return {
      id: event.id,
      title: event.title,
      description: event.description,
      badge,
      tone,
      when: relativeTime(when),
      fullTime: formatDateTime(when),
      extra: isDateNotFound && event.metadata.action_required === 'assign_to_cycle' ? (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1.5">
            No IEP date in "{event.metadata.filename}". Assign it to a cycle:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {(event.metadata.available_cycles || allCycles).map((c) => (
              <button key={c.id} type="button" disabled={!!assigningFile}
                onClick={() => handleAssignFile(event.metadata.file_id, c.id)}
                className="rounded-md border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:bg-transparent dark:border-amber-500/40 dark:text-amber-300">
                {assigningFile?.fileId === event.metadata.file_id && assigningFile?.cycleId === c.id
                  ? 'Assigning…' : (c.label || getCycleLabel(c))}
              </button>
            ))}
          </div>
        </div>
      ) : null,
    };
  });
  const dobMismatchCount = activities.filter((a) => a.event_type === 'dob_mismatch').length;
  const activityNotice = !dobMismatchDismissed && dobMismatchCount > 0 ? (
    <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-1.5 dark:border-amber-500/30 dark:bg-amber-500/10">
      <span className="flex-1 text-xs font-semibold text-amber-900 dark:text-amber-300">
        DOB mismatch — {dobMismatchCount} file{dobMismatchCount !== 1 ? 's' : ''} skipped
      </span>
      <button type="button" onClick={() => setDobMismatchDismissed(true)} aria-label="Dismiss"
        className="text-amber-800 hover:text-amber-950 dark:text-amber-300">
        <NavIcon name="close" className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : null;
  const activityTimeline = (
    <ActivityTimeline
      items={activityItems}
      notice={activityNotice}
      hasMore={activityPage < activityTotal}
      onLoadMore={() => fetchActivity(activityPage + 1)}
    />
  );
  const identityEd = compliance?.identity_timeline?.extracted_data || {};
  const keyDates = [
    { label: 'IEP meeting', iso: compliance?.iep_date, value: formatDate(compliance?.iep_date) },
    {
      label: 'Annual review due',
      iso: compliance?.iep_end_date || student?.iep_due_date,
      value: formatDate(compliance?.iep_end_date || student?.iep_due_date),
    },
    {
      label: 'Re-evaluation due',
      iso: identityEd.reevaluation_due_date || student?.reevaluation_due_date,
      value: formatDate(identityEd.reevaluation_due_date || student?.reevaluation_due_date),
    },
  ];
  const totalIssues = compliance?.total_issues ?? sectionEntries.reduce((n, e) => n + e.issues, 0);

  const rerunDisabled = isRerunActive || !hasStudentFiles;
  const rerunTitle = !hasStudentFiles
    ? 'Upload a file before running compliance'
    : isRerunActive
      ? 'A compliance run is already in progress'
      : 'Re-run compliance';

  return (
    <div className="flex min-h-[calc(100vh-4rem)] w-full min-w-0">
      <ProcessingProgressFloat
        visible={processingProgress.visible}
        percent={processingProgress.percent}
        remainingSeconds={processingProgress.remainingSeconds}
        estimatedSeconds={processingProgress.estimatedSeconds}
        phaseLabel={livePointer?.phaseLabel || processingProgress.phaseLabel || 'Processing…'}
        queued={processingProgress.queued}
        queuePosition={processingProgress.queuePosition}
        started={processingProgress.started}
        awaitingResults={processingProgress.awaitingResults}
        resumed={processingProgress.resumed}
        studentName={student?.student_name || null}
        cycleLabel={liveCycleLabel}
        cycleCount={liveCycleActive ? Number(livePointer?.cycleTotal || processingProgress.cycleTotal) : (allCycles.length > 1 ? allCycles.length : 0)}
        cycleIndex={liveCycleActive ? Number(livePointer?.cycleIndex || processingProgress.cycleIndex || 1) : null}
      />
      {alert && (
        <div className="fixed top-20 right-4 z-50 w-80 sm:w-96">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}
      {queueMessage && !alert && !processingProgress.visible && (
        <div className="fixed top-20 right-4 z-50 w-80 sm:w-96">
          <Alert type="info" message={queueMessage} onClose={() => setQueueMessage(null)} />
        </div>
      )}


      <HumanReviewModal
        open={!!humanReviewModal}
        compliance={compliance}
        sectionConfig={SECTION_CONFIG}
        sectionKey={humanReviewModal?.sectionKey || null}
        sectionKeys={humanReviewModal?.sectionKeys || null}
        title={humanReviewModal?.title}
        onClose={() => setHumanReviewModal(null)}
        onOpenFileAtPage={handleOpenFileAtPage}
        onOpenSource={handleOpenSource}
        onOpenSection={(key) => {
          setHumanReviewModal(null);
          if (SERVICES_SUPPORTS_KEYS.includes(key)) {
            openMergedServices();
          } else {
            openSectionPage(key);
          }
        }}
        onDismissFlag={handleDismissFinding}
      />

      <SourcePanel
        studentId={id}
        request={sourceRequest}
        onClose={() => setSourceRequest(null)}
        onNavigate={(req) => handleOpenSource(req)}
        onOpenFull={(req) => {
          setSourceRequest(null);
          openFullViewer(req.fileId, req.page, req.fileName, req.quote);
        }}
      />

      <DocumentViewer
        open={!!viewerFile}
        file={viewerFile}
        page={viewerPage}
        highlightQuote={viewerHighlightQuote}
        studentId={id}
        onClose={() => { setViewerFile(null); setViewerPage(null); setViewerHighlightQuote(null); }}
      />

      <ConfirmModal
        open={confirmDeleteCycle}
        title="Delete Compliance Cycle"
        message={`Are you sure you want to delete the ${selectedCycle ? getCycleLabel(selectedCycle) : ''} compliance cycle? This action cannot be undone.${isLatestCycle ? '\n\nThis is the latest cycle. Deleting it will also reset the IEP Checklist.' : ''}`}
        confirmText="Delete Cycle"
        variant="danger"
        loading={deletingCycle}
        onConfirm={handleDeleteCycle}
        onCancel={() => { if (!deletingCycle) setConfirmDeleteCycle(false); }}
      />

      <ConfirmModal
        open={!!confirmRerunSection}
        title="Re-run Section"
        message={`Re-run the "${SECTION_CONFIG[confirmRerunSection]?.label || confirmRerunSection}" section? This will re-process it and overwrite the current results for this cycle.`}
        confirmText="Re-run Section"
        variant="info"
        onConfirm={() => { const key = confirmRerunSection; setConfirmRerunSection(null); if (key) handleRerunSection(key); }}
        onCancel={() => setConfirmRerunSection(null)}
      />

      <ConfirmModal
        open={confirmRerunAll}
        title="Re-run Compliance"
        message="Re-run the full compliance check? This will re-process all sections and overwrite the current results for this cycle."
        confirmText="Re-run Compliance"
        variant="info"
        onConfirm={() => { setConfirmRerunAll(false); handleRerunAll(); }}
        onCancel={() => setConfirmRerunAll(false)}
      />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-20 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Left panel: IEP cycles + sections */}
      <aside
        aria-label="Report navigation"
        style={{ '--compliance-nav-w': `${navWidth}px` }}
        className={`
        fixed lg:sticky top-16 left-0 bottom-0 lg:bottom-auto w-[min(88vw,272px)] lg:w-[var(--compliance-nav-w)] lg:h-[calc(100vh-4rem)] bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col z-30 lg:flex-shrink-0
        transform transition-transform duration-300 lg:transform-none
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}
      >
        <div className="flex-1 min-h-0 overflow-hidden px-3 py-4 flex flex-col gap-3.5">
          <div className="flex-shrink-0 max-h-[42%] overflow-y-auto">
            <PanelLabel>IEP cycle</PanelLabel>
            {!showMainContent ? (
              <div className="mt-1.5 space-y-1">
                <div className="h-[52px] rounded-[10px] bg-slate-100 dark:bg-slate-800 animate-pulse" />
                <div className="h-10 rounded-[10px] bg-slate-100 dark:bg-slate-800 animate-pulse" />
              </div>
            ) : cycleList.length === 0 ? (
              <p className="mt-1.5 px-2 text-xs text-slate-500 dark:text-slate-400">No cycles yet</p>
            ) : (
              <div role="tablist" aria-label="IEP cycles" className="mt-1.5 flex flex-col gap-1">
                {cycleList.map((cycle, idx) => {
                  const selected = cycleIdsMatch(cycle.id, selectedCycleId);
                  const date = formatDate(cycle.iep_date || cycle.iep_start_date);
                  return (
                    <div key={cycle.id || `${date}-${idx}`} className="relative group">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => { setSelectedCycleId(cycle.id); setSidebarOpen(false); }}
                        className={`w-full text-left text-[13px] px-2.5 py-2 rounded-[10px] border transition-colors ${
                          selected
                            ? 'border-indigo-200 bg-indigo-50 text-indigo-950 dark:border-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-100'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span className="flex items-start gap-1.5 pr-6">
                          <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${cycleStatusDot(cycle.overall_status)}`} aria-hidden="true" />
                          <span className="min-w-0">
                            <b className="block">{date}</b>
                            <span className="block text-[12px] leading-snug break-words">
                              {cycle._pending
                                ? (cycle.overall_status === 'processing' ? 'Scoring this IEP…' : 'Waiting in queue')
                                : `${formatCycleType(cycle.cycle_type) || 'IEP'} · ${(selected && score != null ? score : cycle.compliance_score) ?? 0}%`}
                            </span>
                          </span>
                        </span>
                        {idx === 0 && (
                          <span className={`block text-xs mt-0.5 pl-3.5 break-words ${selected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                            Current IEP{cycle.iep_end_date ? ` · through ${formatDate(cycle.iep_end_date)}` : cycle._pending ? ' · results appear when this cycle finishes' : ''}
                          </span>
                        )}
                      </button>
                      {selected && !cycle._pending && (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteCycle(true)}
                          disabled={hasProcessing || deletingCycle}
                          aria-label={`Delete the ${date} cycle`}
                          title={hasProcessing ? 'Wait for processing to finish' : 'Delete this cycle'}
                          className="absolute top-1.5 right-1.5 p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-red-500/10"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <nav aria-label="Sections" className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 text-sm">
            <PanelLabel className="py-1">Sections</PanelLabel>
            <button
              type="button"
              onClick={() => { setSidebarOpen(false); navigate(`/students/${id}/information`); }}
              aria-current={!routeSectionKey ? 'page' : undefined}
              className={`flex items-center gap-2.5 px-2 py-[7px] rounded-lg text-left ${
                !routeSectionKey ? 'bg-indigo-50 font-semibold text-indigo-900 dark:bg-indigo-500/15 dark:text-indigo-100' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <NavIcon name="home" className="w-[18px] h-[18px]" />
              Overview
            </button>
            {showMainContent && sectionEntries.map((entry) => (
              <SectionNavItem
                key={entry.key}
                entry={entry}
                active={activeSection === entry.key}
                onOpen={openEntry}
                onOpenPage={openEntryPage}
              />
            ))}
            {showMainContent && noCompliance && (
              <p className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400">Sections appear after the first compliance run.</p>
            )}
          </nav>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 p-2 flex-shrink-0 flex flex-col gap-1">
          {showMainContent && !noCompliance && (
            <MenuButton label="Export" className="w-full" align="left" drop="up">
              <MenuItem icon={<PdfFileIcon />} onClick={handleExportPdf} disabled={!compliance || exportingPdf}>
                {exportingPdf ? 'Exporting PDF…' : 'PDF report'}
              </MenuItem>
              <MenuItem icon={<ExcelFileIcon />} onClick={handleExportCsv} disabled={!compliance || exportingCsv}>
                {exportingCsv ? 'Exporting Excel…' : 'Excel workbook'}
              </MenuItem>
            </MenuButton>
          )}
          <Link to={`/students/${id}/files`} className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-[13px] text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>
            Student files
          </Link>
        </div>
        <button
          type="button"
          aria-label="Resize navigation"
          title="Drag to resize"
          className="hidden lg:block absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-40 hover:bg-indigo-400/40 active:bg-indigo-500/50"
          onMouseDown={(e) => {
            e.preventDefault();
            draggingNav.current = true;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
        />
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 w-full relative">
        {deletingCycle && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-slate-950/70 backdrop-blur-[1px]">
            <Loader text="Deleting compliance cycle..." />
          </div>
        )}
        {/* Sticky top bar — opens the report panel on small screens */}
        <div className="sticky top-16 z-10 bg-white/95 backdrop-blur dark:bg-slate-900/95 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 py-2 flex items-center gap-2 lg:hidden">
          <button type="button" onClick={() => setSidebarOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border border-slate-300 text-[13px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
            <NavIcon name="menu" className="w-4 h-4" />
            Cycles &amp; sections
          </button>
          {selectedCycle && (
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {formatDate(selectedCycle.iep_date || selectedCycle.iep_start_date)} · {formatCycleType(selectedCycle.cycle_type)}
            </span>
          )}
        </div>

        {/* Scrollable content */}
        <div>
        {loading ? (
          <ReportSkeleton />
        ) : loadError || !student ? (
          <div className="p-4 sm:p-6 flex items-center justify-center min-h-[calc(100vh-8rem)]">
            <div className="max-w-md w-full text-center bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Could not load compliance report</h2>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
                {loadError || 'Student data is unavailable.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
                <button
                  type="button"
                  onClick={() => { setLoading(true); fetchData(); }}
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/students')}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 text-sm font-medium"
                >
                  Back to students
                </button>
              </div>
            </div>
          </div>
        ) : routeSectionKey ? (
        <div className="w-full min-w-0 p-3 sm:p-4 md:p-5 lg:p-6">
          {renderSectionPage()}
        </div>
        ) : (
        <div className="w-full min-w-0 p-3 sm:p-4 md:p-5 lg:p-6 flex flex-col gap-4 sm:gap-5">
          {/* Summary card */}
          <section aria-label="Student summary"
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_minmax(220px,280px)] gap-4 sm:gap-5 items-center">
            <ScoreRing score={score}>
              <ProfileAvatar
                type="student"
                id={student?.id}
                hasProfileImage={!!student?.has_profile_image}
                containerClassName="rounded-full"
              />
            </ScoreRing>
            <div className="flex flex-col gap-3 min-w-0">
              <div className="flex items-baseline gap-x-2.5 gap-y-1 flex-wrap">
                <h1 className="text-[22px] font-bold text-slate-900 dark:text-white">{student.student_name}</h1>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {[
                    gradeLabel !== '—' ? gradeLabel : null,
                    dobLabel !== '—' ? `DOB ${dobLabel}` : null,
                    student.school_name,
                    `Case manager ${student.sped_teacher?.name || student.case_manager || '—'}`,
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
              <KeyDates items={keyDates} />
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:col-span-2 xl:col-span-1 xl:flex-col xl:flex-nowrap xl:items-stretch">
              <button type="button" onClick={requestRerunAll} disabled={rerunDisabled} title={rerunTitle}
                className="w-full h-10 px-4 rounded-[10px] bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-500 dark:hover:bg-indigo-400">
                {rerunning ? 'Running…' : 'Re-run compliance'}
              </button>
              {humanReviewCount > 0 && (
                <button type="button"
                  onClick={() => setHumanReviewModal({ sectionKey: null, sectionKeys: DISPLAY_SECTION_KEYS })}
                  className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-[10px] bg-amber-500 px-3 text-[13px] font-semibold text-white hover:bg-amber-600">
                  {actionableHumanReviewFlags.length || humanReviewCount} item{(actionableHumanReviewFlags.length || humanReviewCount) === 1 ? '' : 's'} to review
                  <span aria-hidden="true">→</span>
                </button>
              )}
              <div className="flex flex-wrap gap-2 w-full">
                <button type="button"
                  onClick={() => navigate(`/students/${id}/iep-checklist`)}
                  title="IEP checklist"
                  className="flex-1 min-w-[4.75rem] h-9 inline-flex items-center justify-center gap-1.5 rounded-[10px] border border-slate-300 bg-white px-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                  IEP
                  {displayIepPct != null && <b className="tabular-nums text-indigo-700 dark:text-indigo-300">{displayIepPct}%</b>}
                </button>
                <button type="button"
                  onClick={() => navigate(`/students/${id}/eligibility_checklist`)}
                  title="Eligibility checklist"
                  className="flex-1 min-w-[5.5rem] h-9 inline-flex items-center justify-center gap-1.5 rounded-[10px] border border-slate-300 bg-white px-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                  Eligibility
                  {displayEligPct != null && <b className="tabular-nums text-indigo-700 dark:text-indigo-300">{displayEligPct}%</b>}
                </button>
              </div>
            </div>
          </section>

          {complianceError && !compliance && (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              <span className="flex-1 min-w-0">{complianceError}</span>
              <button type="button" onClick={() => fetchData()}
                className="h-9 px-4 rounded-[10px] bg-white border border-red-300 font-semibold hover:bg-red-100 dark:bg-transparent dark:border-red-500/40 dark:hover:bg-red-500/10">
                Retry
              </button>
            </div>
          )}

          {/* Results on the way while the first upload is processed */}
          {noCompliance && !compliance && processingProgress.visible && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-10 text-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Results are on the way</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-2 max-w-md mx-auto">
                The uploaded documents are being processed. Drag the progress card to move it. Section results appear here as each IEP cycle finishes — no need to refresh.
              </p>
            </div>
          )}

          {/* No compliance empty state */}
          {noCompliance && !compliance && !processingProgress.visible && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-12 text-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">No compliance data yet</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-2 max-w-md mx-auto">
                {hasStudentFiles
                  ? 'Re-run compliance on the files already uploaded, or add more documents.'
                  : "Upload this student's IEP documents to start the analysis."}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                <button type="button" onClick={() => navigate(`/students/${id}/files/add`)}
                  className="h-10 px-5 rounded-[10px] bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold w-full sm:w-auto">
                  Upload documents
                </button>
                <button type="button" onClick={requestRerunAll} disabled={rerunDisabled} title={rerunTitle}
                  className="h-10 px-5 rounded-[10px] border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
                  {rerunning ? 'Running…' : 'Re-run compliance'}
                </button>
              </div>
            </div>
          )}

          {/* Sections */}
          {compliance && (
            <div className="relative flex flex-col gap-4" aria-busy={Boolean(cycleSwitching)}>
              {cycleSwitching && (
                <div className="absolute inset-0 z-20 flex items-start justify-center pt-16 rounded-2xl bg-[#f6f7fb]/80 dark:bg-slate-950/80 backdrop-blur-[2px]">
                  <div className="flex items-center gap-2.5 px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg">
                    <svg className="animate-spin w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Loading {cycleSwitching}…</p>
                  </div>
                </div>
              )}
              <FilterChips value={cardFilter} onChange={setCardFilter} counts={chipCounts} />
              {visibleEntries.length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">No sections match this filter.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
                  {visibleEntries.map((entry) => (
                    <SectionSummaryCard key={entry.key} entry={entry} onOpen={openEntry} onReview={reviewEntry} onOpenPage={openEntryPage} />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
            {activityTimeline}
          </div>
        </div>
        )}
        </div>
      </div>
    </div>
  );
}
